import React, { useState, useRef, useEffect, useMemo } from "react";
import { useLazyOtherChargeOptions, applyOtherChargeToEntry, queryHasTwoLetters } from "../utils/otherChargeOptions";
import ReactDOM from "react-dom";
import { Icon } from "@iconify/react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { BiCheckCircle } from "react-icons/bi"; // Ensure you have this import for the icon
import toast, { Toaster } from "react-hot-toast";
import {
  setupPortalContainers,
  teardownPortalContainers,
} from "../utils/portalContainers";
import "../assets/css/theme-table-header.css";
import {
  CUSTOM_ORDER_LINE_ERROR_OPTIONS,
  collectLineItemFieldErrors,
  lineCellErrorStyle,
  pruneLineFieldErrors,
} from "../utils/lineItemFieldErrors";
import { getUserData, node_imageBaseUrl } from "./const";
import { useIndiaStateCity } from "../utils/useIndiaStateCity";
import { getActiveItems } from "../utils/statusUtils";
import {
  createSupplier,
  getErpAuthState,
  listBankAccounts,
  listBillTypes,
  listSuppliers,
  listCustomerUsers,
  showCustomerUser,
  showOrderQueue,
  listOrderQueue,
  isErpApiSuccess,
  createCustomOrderMultipartForm,
} from "../erpClient";
import {
  fetchProductGroupOptionById,
  mergeProductGroupOptions,
  searchProductGroupSelectOptions,
} from "../utils/productGroupOptions";
import { numPool } from "../utils/productGroupPool";
import ProductGroupPoolBalance from "../components/ProductGroupPoolBalance";
import PaymentSection, {
  PAYMENT_METHOD_LABELS,
} from "../components/PaymentSection";
import { useLocation, useNavigate } from "react-router-dom";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import OtherAmountModal from "../components/OtherAmountModal";
import {
  calculateOtherAmountAmount as calculateAmount,
  calculateOtherAmountLessWeight as calculateLessWeight,
  sumOtherAmountEntries,
  mapOtherAmountEntriesForCreate,
} from "../utils/otherAmountCalculations";

import CustomerSelection from "../components/CustomerSelection";
import {
  getMobileValidationError,
  isValidMobileNumber,
} from "../utils/mobileInput";
import { getEmailValidationError } from "../utils/emailInput";
import { handleRoundOffInputChange } from "../utils/roundOffInput";
import {
  autoRoundOff,
  formatAutoRoundOffIndicator,
} from "../utils/autoRoundOff";
import { buildStandardPaymentLines } from "../utils/paymentLinesErpAdapter";
import { createCustomOrderIdempotencyKey } from "../utils/customOrdersErpAdapter";
import {
  resolveQueueDetailsId,
  findOrderQueueInResponse,
} from "../utils/queueNavigation";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import Select from "react-select";
import {
  billTypeHeaderSelectStyles,
  billTypeHeaderSelectWrapStyle,
} from "../utils/billTypeHeaderSelectStyles";

function pgDropdownLabel(item) {
  if (!item || typeof item !== "object") return "";
  if (item.name) return String(item.name);
  return String(item.label || "")
    .split(" — pool:")[0]
    .trim();
}

const customSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "100%",
    height: "100%",
    width: "100%",
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
    backgroundColor: "transparent",
    "&:hover": {
      border: "none",
      cursor: "pointer",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 8px",
    height: "100%",
    display: "flex",
    alignItems: "center",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    color: "#000",
  }),
  singleValue: (base) => ({
    ...base,
    margin: 0,
    color: "#000",
    fontSize: "0.85rem",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: "100%",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "4px",
    color: "#6c757d",
    display: "none", // Hide the dropdown arrow to look more like a plain cell until interacted
  }),
  indicatorSeparator: (base) => ({
    display: "none",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    fontSize: "0.85rem",
    width: "100%", // Ensure menu matches width
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "var(--theme-primary)"
      : state.isFocused
        ? "#f8f9fa"
        : "white",
    color: state.isSelected ? "white" : "#000",
    "&:active": {
      backgroundColor: "var(--theme-primary)",
      color: "white",
    },
  }),
  container: (base) => ({
    ...base,
    width: "100%",
    height: "100%",
    padding: 0,
    margin: 0,
  }),
};

const isZeroOrNullValue = (value) =>
  value === 0 ||
  value === "0" ||
  value === null ||
  value === undefined ||
  value === "";


const SUMMARY_LABEL_STYLE = {
  width: "110px",
  fontSize: "0.85rem",
  marginBottom: 0,
  flexShrink: 0,
  fontWeight: 600,
};

const SUMMARY_INPUT_WRAPPER_STYLE = {
  flexGrow: 1,
  minWidth: 0,
};

const normalizeDesignPhotoPath = (raw) => {
  if (raw == null || raw === "") return "";
  if (Array.isArray(raw)) return normalizeDesignPhotoPath(raw[0]);
  if (typeof raw === "object") {
    return normalizeDesignPhotoPath(raw.url || raw.path || raw.image || "");
  }
  const s = String(raw).trim();
  if (!s) return "";
  if (s.startsWith("[") || s.startsWith("{")) {
    try {
      return normalizeDesignPhotoPath(JSON.parse(s));
    } catch (_) {
      /* use raw string */
    }
  }
  if (s.includes(",")) return s.split(",")[0].trim();
  return s;
};

const getProductImageCandidates = (imagePath) => {
  const p = normalizeDesignPhotoPath(imagePath);
  if (!p) return [];

  if (
    p.startsWith("data:") ||
    p.startsWith("blob:") ||
    p.startsWith("http://") ||
    p.startsWith("https://")
  ) {
    return [p];
  }

  const base = String(node_imageBaseUrl || "").replace(/\/+$/, "");
  const normalized = p.replace(/^\/+/, "");

  if (normalized.startsWith("upload/") || normalized.startsWith("uploads/")) {
    return [encodeURI(`${base}/${normalized}`)];
  }

  if (normalized.includes("/")) {
    return [
      encodeURI(`${base}/${normalized}`),
      encodeURI(`${base}/upload/${normalized}`),
    ];
  }

  // Plain filename from order queue or custom order uploads
  return [
    encodeURI(`${base}/upload/order_queue/${normalized}`),
    encodeURI(`${base}/upload/custom_order/${normalized}`),
    encodeURI(`${base}/uploads/order_queue/${normalized}`),
    encodeURI(`${base}/uploads/custom_order/${normalized}`),
    encodeURI(`${base}/customorder/${normalized}`),
    encodeURI(`${base}/upload/${normalized}`),
  ];
};

const getProductImageUrl = (imagePath) =>
  getProductImageCandidates(imagePath)[0] || null;

const handleProductImageError = (event) => {
  const rawPath = event.currentTarget.dataset.rawPath;
  if (!rawPath) return;
  const candidates = getProductImageCandidates(rawPath);
  const nextIndex = Number(event.currentTarget.dataset.candidateIndex || 0) + 1;
  if (nextIndex < candidates.length) {
    event.currentTarget.dataset.candidateIndex = String(nextIndex);
    event.currentTarget.src = candidates[nextIndex];
    return;
  }
  event.currentTarget.style.display = "none";
};

const normalizeCustomerUser = (user = {}) => ({
  id: user.id,
  fullName: user.name || user.fullName || "",
  name: user.name || user.fullName || "",
  mobile: user.mobile || "",
  email: user.email || "",
  city: user.city || "",
  address: user.address || "",
  state: user.state || "",
  area: user.area || "",
  aadharNo: user.aadhar_no || user.aadharNo || "",
  panNo: user.pan_no || user.panNo || "",
  birthDate: user.birth_date || user.birthDate || "",
  maritalStatus: user.marital_status || user.maritalStatus || "",
  anniversaryDate: user.anniversary_date || user.anniversaryDate || "",
  balance: user.users_balance ?? user.balance ?? "",
  saleBalance: user.sale_balance || user.saleBalance || "",
  companyName: user.company_name || user.companyName || "",
  companyAddress: user.company_address || user.companyAddress || "",
  gstNumber: user.GST_number || user.gstNumber || "",
  reference: user.reference || "",
  status: user.status || "",
});

const mergeCustomerUsers = (existingUsers = [], incomingUsers = []) => {
  const merged = [...existingUsers];

  incomingUsers.forEach((incomingUser) => {
    const existingIndex = merged.findIndex(
      (user) =>
        String(user.id) === String(incomingUser.id) ||
        (user.mobile && incomingUser.mobile && user.mobile === incomingUser.mobile)
    );

    if (existingIndex >= 0) {
      merged[existingIndex] = { ...merged[existingIndex], ...incomingUser };
    } else {
      merged.push(incomingUser);
    }
  });

  return merged.sort((a, b) => Number(a.id || 0) - Number(b.id || 0));
};

const normalizeKarigar = (karigar = {}) => ({
  id: karigar.id,
  fullName: karigar.name || karigar.fullName || "",
  companyName: karigar.company_name || karigar.companyName || "",
  shortCode: karigar.short_code || karigar.shortCode || "",
  contactNumber: karigar.contact_number || karigar.contactNumber || "",
  email: karigar.email || "",
  address: karigar.address || "",
  city: karigar.city || "",
  gstNumber: karigar.gst_number || karigar.GST_number || karigar.gstNumber || "",
  status: karigar.status || "",
  isPrimary:
    karigar.primary_supplier === 1 || karigar.primary_supplier === "1" ? 1 : 0,
});

const mergeKarigars = (existingKarigars = [], incomingKarigars = []) => {
  const merged = [...existingKarigars];

  incomingKarigars.forEach((incomingKarigar) => {
    const existingIndex = merged.findIndex(
      (karigar) => String(karigar.id) === String(incomingKarigar.id)
    );

    if (existingIndex >= 0) {
      merged[existingIndex] = { ...merged[existingIndex], ...incomingKarigar };
    } else {
      merged.push(incomingKarigar);
    }
  });

  return merged.sort((a, b) => Number(b.id || 0) - Number(a.id || 0));
};

const CustomOrderEntry = ({ masterGoldRate, masterSilverRate }) => {
  const { buttonClass, buttonTableStyle, cardClass } =
    useButtonTableStyle();
  const location = useLocation();
  const navigate = useNavigate();

  const [canAddCustomOrder, setCanAddCustomOrder] = useState(() =>
    hasPermission(PERMISSIONS.CUSTOM_ORDER_ADD),
  );

  useEffect(() => {
    const syncPerms = () => {
      setCanAddCustomOrder(hasPermission(PERMISSIONS.CUSTOM_ORDER_ADD));
    };
    window.addEventListener("permissionsUpdated", syncPerms);
    window.addEventListener("erpAuthUpdated", syncPerms);
    return () => {
      window.removeEventListener("permissionsUpdated", syncPerms);
      window.removeEventListener("erpAuthUpdated", syncPerms);
    };
  }, []);
  const queueDetailsId = resolveQueueDetailsId(location.state);
  const customOrderId = location.state?.customOrderId;
  const customerUserId = location.state?.customerUserId;
  const [customOrderData, setCustomOrderData] = useState(null);
  const [isLoadingOrderData, setIsLoadingOrderData] = useState(false);
  const queueHydrationKeyRef = useRef(null);
  const [userss, setUserss] = useState([]);
  const customerSearchCacheRef = useRef(new Set());
  const karigarSearchCacheRef = useRef(new Set());

  const applyCustomerToForm = (customer) => {
    if (!customer) return;
    if (customer.id != null && customer.id !== "") {
      setSelectedCustomerId(customer.id);
    }
    setCustomerMobile(customer.mobile || "");
    setCustomerMobileSearchText(customer.mobile || "");
    setCustomerName(customer.fullName || customer.name || "");
    setCustomerNameSearchText(customer.fullName || customer.name || "");
    setCustomerAddress(customer.address || "");
    setCustomerCity(customer.city || "");
    if (customer.state) {
      setSelectedState(customer.state);
    }
    setCustomerDetails((prevDetails) => ({
      ...prevDetails,
      email: customer.email || "",
      birthDate: customer.birthDate || "",
      anniversaryDate: customer.anniversaryDate || "",
      companyName: customer.companyName || "",
      companyAddress: customer.companyAddress || "",
      gstNumber: customer.gstNumber || "",
      aadharNo: customer.aadharNo || "",
      panNo: customer.panNo || "",
      maritalStatus: customer.maritalStatus || "",
      balance: customer.balance || "",
      saleBalance: customer.saleBalance || "",
      reference: customer.reference || "",
      state: customer.state || "",
      area: customer.area || "",
    }));
  };

  const extractCustomerFromShowResponse = (response) => {
    const d = response?.data;
    if (!d) return null;
    return (
      d.customer ??
      d.user ??
      d.item ??
      (Array.isArray(d.customers) ? d.customers[0] : null) ??
      (Array.isArray(d.items) ? d.items[0] : null) ??
      (Array.isArray(d.data) ? d.data[0] : null) ??
      (Array.isArray(d) ? d[0] : null) ??
      (d.id != null ? d : null)
    );
  };

  const applyQueueOrderToCustomOrder = async (foundOrder) => {
    if (!foundOrder) return;
    setCustomOrderData(foundOrder);

    const custId =
      foundOrder.customer_user_id ??
      foundOrder.user_id ??
      foundOrder.customer_id ??
      customerUserId;

    const snapshotName =
      foundOrder.name ||
      foundOrder.user_name ||
      foundOrder.customer_name ||
      "";
    const snapshotMobile =
      foundOrder.mobile ||
      foundOrder.user_mobile ||
      foundOrder.customer_mobile ||
      "";
    if (snapshotName || snapshotMobile || custId) {
      applyCustomerToForm({
        id: custId || null,
        fullName: snapshotName,
        name: snapshotName,
        mobile: snapshotMobile,
        address:
          foundOrder.address ||
          foundOrder.user_address ||
          foundOrder.customer_address ||
          "",
        city:
          foundOrder.city ||
          foundOrder.user_city ||
          foundOrder.customer_city ||
          "",
        area: foundOrder.area || location.state?.area || "",
        state: foundOrder.state || foundOrder.user_state || "",
      });
    }

    if (custId) {
      try {
        const response = await showCustomerUser({ id: Number(custId) });
        const raw = extractCustomerFromShowResponse(response);
        if (isErpApiSuccess(response) && raw) {
          const normalizedCustomer = normalizeCustomerUser(raw);
          setUserss((prevUsers) =>
            mergeCustomerUsers(prevUsers, [normalizedCustomer]),
          );
          applyCustomerToForm(normalizedCustomer);
        }
      } catch (custErr) {
        console.error("Error loading queue customer:", custErr);
      }
    }

    if (foundOrder.products && foundOrder.products.length > 0) {
      const uniquePgIds = [
        ...new Set(
          foundOrder.products
            .map(
              (product) =>
                product.product_group_id ?? product.product_group,
            )
            .filter((id) => id != null && id !== ""),
        ),
      ];
      const resolvedPgs = await Promise.all(
        uniquePgIds.map(async (id) => {
          const productWithName = foundOrder.products.find(
            (p) =>
              String(p.product_group_id ?? p.product_group) === String(id) &&
              p.product_group_name,
          );
          const labelHint = productWithName?.product_group_name;
          const resolved = await resolveProductGroupOption(id, labelHint);
          if (resolved) return resolved;
          if (labelHint) {
            return {
              value: id,
              label: labelHint,
              name: labelHint,
            };
          }
          return null;
        }),
      );
      const validResolvedPgs = resolvedPgs.filter(Boolean);
      // Cache so submit / rate lookup treat queue PG as already selected
      if (validResolvedPgs.length > 0) {
        productGroupOptionsCacheRef.current = mergeProductGroupOptions(
          productGroupOptionsCacheRef.current,
          validResolvedPgs,
        );
        applyPoolHeaderFromGroup(validResolvedPgs[0]);
      }

      const pgById = {};
      validResolvedPgs.forEach((pg) => {
        const label = pg.label || pg.name || `Group ${pg.value}`;
        pgById[pg.value] = label;
        pgById[String(pg.value)] = label;
      });

      const mappedProducts = foundOrder.products.map((product) => {
        const pgId = product.product_group_id ?? product.product_group;
        const productGroupName =
          pgById[pgId] ||
          pgById[String(pgId)] ||
          product.product_group_name ||
          pgId ||
          "N/A";
        return {
          ...product,
          product_group: pgId,
          product_group_name: productGroupName,
        };
      });

      const firstDelivery = mappedProducts.find(
        (p) => p.delivery_date,
      )?.delivery_date;
      if (firstDelivery) {
        setExpDate(String(firstDelivery).slice(0, 10));
      }

      const tableData = mappedProducts.map((product, index) => {
        const row = {
          id: index + 1,
          data: COLUMNS.map((col) => {
            let value = "";
            let additionalProps = {};
            switch (col.id) {
              case "productGroup":
                value = product.product_group_name || "";
                additionalProps.productGroupId =
                  product.product_group_id ?? product.product_group ?? null;
                break;
              case "design":
                value = product.description || "";
                break;
              case "size":
                value = product.size || "";
                break;
              case "pcs":
                value = product.pcs || "";
                break;
              case "grWt":
                value = product.expected_weight || "";
                break;
              case "lsWt":
                value = "0";
                break;
              case "netWt":
                value = product.expected_weight || "";
                break;
              case "itRateOn":
                value = product.it_rate_on || product.itRateOn || "NWt";
                break;
              case "itRate":
                value = product.price_value || "";
                break;
              case "itAmt":
                value = product.expected_price || "";
                break;
              case "lType":
                value = "";
                break;
              case "lbrPercentage":
                value = "";
                break;
              case "lbrRate":
                value = "";
                break;
              case "lbrAmt":
                value = "";
                break;
              case "othAmt":
                value = "";
                break;
              case "mrp":
                value = "";
                break;
              case "image": {
                const rawImagePath = normalizeDesignPhotoPath(
                  product.design_photos ||
                  product.design_photo ||
                  product.image ||
                  "",
                );
                value = rawImagePath;
                break;
              }
              case "totalAmt":
                value = "";
                break;
              default:
                value = "";
            }
            if (col.id === "image") {
              const imagePreview = getProductImageUrl(value);
              return { id: col.id, value, imageFile: null, imagePreview };
            }
            return { id: col.id, value, ...additionalProps };
          }),
        };

        // Auto-calc TotalAmt on queue hydrate (same as calculateTotalAmount)
        const itAmt =
          parseFloat(row.data.find((c) => c.id === "itAmt")?.value) || 0;
        const lbrAmt =
          parseFloat(row.data.find((c) => c.id === "lbrAmt")?.value) || 0;
        const othAmt =
          parseFloat(row.data.find((c) => c.id === "othAmt")?.value) || 0;
        const totalAmtCell = row.data.find((c) => c.id === "totalAmt");
        if (totalAmtCell) {
          totalAmtCell.value = (itAmt + lbrAmt + othAmt).toFixed(2);
        }

        return row;
      });

      setTableData(tableData);
    }
  };

  const fetchOrderData = async (orderId) => {
    if (!orderId) return;

    setIsLoadingOrderData(true);
    try {
      let foundOrder = findOrderQueueInResponse(
        await showOrderQueue({ id: orderId }),
        orderId,
      );

      if (!foundOrder) {
        foundOrder = findOrderQueueInResponse(
          await listOrderQueue({
            search: String(orderId),
            page: 1,
            limit: 50,
          }),
          orderId,
        );
      }

      if (foundOrder) {
        await applyQueueOrderToCustomOrder(foundOrder);
      } else {
        toast.error("Order not found");
      }
    } catch (error) {
      console.error("Error fetching order data:", error);
      toast.error("Failed to fetch order data");
    } finally {
      setIsLoadingOrderData(false);
    }
  };

  // Fetch customer users only when they are needed.
  const fetchUserss = async (search = "") => {
    const searchText =
      search !== undefined && search !== null ? String(search).trim() : "";

    try {
      const response = await listCustomerUsers({ search: searchText });
      const usersData = Array.isArray(response?.data?.customers)
        ? response.data.customers
        : Array.isArray(response?.data?.items)
          ? response.data.items
          : Array.isArray(response?.data)
            ? response.data
            : [];

      if (isErpApiSuccess(response) && Array.isArray(usersData)) {
        const normalizedUsers = usersData.map((user) => normalizeCustomerUser(user));

        setUserss((prevUsers) =>
          searchText
            ? mergeCustomerUsers(prevUsers, normalizedUsers)
            : mergeCustomerUsers([], normalizedUsers)
        );

        if (searchText) {
          customerSearchCacheRef.current.add(searchText.toLowerCase());
        }

        return normalizedUsers;
      } else {
        console.error("Unexpected customer users API response format:", response);
      }
    } catch (error) {
      console.error("Error fetching customer users:", error);
    }

    return [];
  };

  const fetchCustomerById = async (customerId) => {
    if (customerId === undefined || customerId === null || customerId === "") {
      return null;
    }

    try {
      const response = await showCustomerUser({ id: customerId });
      const rawCustomer =
        response?.data?.customer ??
        response?.data?.user ??
        response?.data?.item ??
        (Array.isArray(response?.data?.customers)
          ? response.data.customers[0]
          : null) ??
        (Array.isArray(response?.data?.items) ? response.data.items[0] : null) ??
        (Array.isArray(response?.data?.data) ? response.data.data[0] : null) ??
        (Array.isArray(response?.data) ? response.data[0] : null);

      if (isErpApiSuccess(response) && rawCustomer) {
        const normalizedCustomer = normalizeCustomerUser(rawCustomer);
        setUserss((prevUsers) => mergeCustomerUsers(prevUsers, [normalizedCustomer]));
        return normalizedCustomer;
      }

      console.error("Unexpected customer response format:", response);
    } catch (error) {
      console.error("Error fetching customer by ID:", error);
    }

    return null;
  };

  // Load queue order when arriving from Order Queue (COPRO → Custom Order entry)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const seeded = location.state?.customOrderData;
    const hydrationKey = queueDetailsId
      ? `queue:${queueDetailsId}`
      : seeded?.id != null
        ? `seeded:${seeded.id}`
        : null;
    if (!hydrationKey) return;
    if (queueHydrationKeyRef.current === hydrationKey) return;
    queueHydrationKeyRef.current = hydrationKey;

    if (seeded) {
      setIsLoadingOrderData(true);
      applyQueueOrderToCustomOrder(seeded).finally(() =>
        setIsLoadingOrderData(false),
      );
      return;
    }
    fetchOrderData(queueDetailsId);
  }, [queueDetailsId, location.state?.customOrderData]);

  // Pre-fill customer data when customerUserId is provided (skip when queue hydrates)
  useEffect(() => {
    if (!customerUserId || queueDetailsId) return;

    const customerUser = userss.find(
      (user) => String(user.id) === String(customerUserId)
    );

    if (customerUser) {
      setCustomerName(customerUser.name || customerUser.fullName || "");
      setCustomerNameSearchText(
        customerUser.name || customerUser.fullName || ""
      );
      setCustomerMobile(customerUser.mobile || "");
      setCustomerMobileSearchText(customerUser.mobile || "");
      setSelectedCustomerId(customerUser.id);
      setCustomerCity(customerUser.city || "");
      setCustomerAddress(customerUser.address || "");
      setCustomerDetails((prevDetails) => ({
        ...prevDetails,
        email: customerUser.email || "",
        birthDate: customerUser.birthDate || "",
        anniversaryDate: customerUser.anniversaryDate || "",
        companyName: customerUser.companyName || "",
        companyAddress: customerUser.companyAddress || "",
        gstNumber: customerUser.gstNumber || "",
        aadharNo: customerUser.aadharNo || "",
        panNo: customerUser.panNo || "",
        maritalStatus: customerUser.maritalStatus || "",
        balance: customerUser.balance || "",
        saleBalance: customerUser.saleBalance || "",
        reference: customerUser.reference || "",
        state: customerUser.state || "",
      }));
      return;
    }

    fetchCustomerById(customerUserId);
  }, [customerUserId, userss, queueDetailsId]);

  // Listen for API URL changes and clear cached lookup data.
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log(
        "API URL changed in CustomOrderEntry, clearing cached lookup data...",
        event.detail
      );
      setUserss([]);
      setKarigars([]);
      customerSearchCacheRef.current.clear();
      karigarSearchCacheRef.current.clear();
      productGroupOptionsCacheRef.current = [];
      setDropdownData((prev) => ({
        ...prev,
        productGroup: [],
      }));
      setLastSearchedProductGroupPrefix("");
      setProductGroupApiCalled(false);
      setProductGroupSearchLoading(false);
      setProductGroupPoolHeader(null);
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, []);

  const applyPoolHeaderFromGroup = (group) => {
    if (!group) {
      setProductGroupPoolHeader(null);
      return;
    }
    setProductGroupPoolHeader({
      pcs: numPool(group.pool_pcs),
      nwt: numPool(group.pool_nwt),
    });
  };

  const getProductGroupOptionsList = () =>
    mergeProductGroupOptions(
      productGroupOptionsCacheRef.current,
      dropdownData.productGroup,
    );

  const resolveProductGroupOption = async (id, labelHint) => {
    if (id != null && id !== "") {
      const cached = getProductGroupOptionsList().find(
        (pg) => String(pg.value) === String(id),
      );
      if (cached) return cached;
      const fetched = await fetchProductGroupOptionById(id);
      if (fetched) {
        productGroupOptionsCacheRef.current = mergeProductGroupOptions(
          productGroupOptionsCacheRef.current,
          [fetched],
        );
        return fetched;
      }
    }
    if (labelHint) {
      return getProductGroupOptionsList().find(
        (pg) =>
          pg.label === labelHint ||
          pg.name === labelHint ||
          pg.short_code === labelHint,
      );
    }
    return null;
  };

  const fetchProductGroupByPrefix = async (prefix = "") => {
    const trimmed = String(prefix || "").trim();
    if (trimmed.length < 3) {
      setDropdownData((prev) => ({ ...prev, productGroup: [] }));
      setProductGroupApiCalled(false);
      setProductGroupSearchLoading(false);
      return [];
    }

    if (lastSearchedProductGroupPrefix === trimmed) {
      setProductGroupApiCalled(true);
      return dropdownData.productGroup;
    }

    setProductGroupSearchLoading(true);
    try {
      const opts = await searchProductGroupSelectOptions(trimmed);
      productGroupOptionsCacheRef.current = mergeProductGroupOptions(
        productGroupOptionsCacheRef.current,
        opts,
      );
      setDropdownData((prev) => ({
        ...prev,
        productGroup: opts,
      }));
      setLastSearchedProductGroupPrefix(trimmed);
      setProductGroupApiCalled(true);
      if (opts.length > 0) {
        applyPoolHeaderFromGroup(opts[0]);
      }
      return opts;
    } catch (error) {
      console.error("Error searching product groups:", error);
      setDropdownData((prev) => ({ ...prev, productGroup: [] }));
      setProductGroupApiCalled(true);
      return [];
    } finally {
      setProductGroupSearchLoading(false);
    }
  };

  // Add new function to calculate per gram rate
  const calculatePerGramRate = (rate, metalType) => {
    return rate;
  };

  // Calculate rate based on purity
  const calculatePurityBasedRate = (purity, rate, metalType) => {
    // Extract the karat/purity value from string (e.g. "18K" -> 18 or "925" -> 925)
    const purityValue = parseFloat(purity);
    if (isNaN(purityValue)) return rate; // If purity is invalid, return base rate

    if (metalType === "Gold") {
      return rate * (purityValue / 24);
    } else if (metalType === "Silver") {
      return (rate * purityValue) / 999;
    }

    return rate; // Default case if metal type is not recognized
  };

  const COLUMNS = [
    { id: "no", label: "No.", width: "20px", value: (row) => row.no },
    {
      id: "productGroup",
      label: (
        <>
          Product Group 
        </>
      ),
      width: "160px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.productGroup}
          onChange={(e) => onChange(row.id, "productGroup", e.target.value)}
        />
      ),
    },
    {
      id: "design",
      label: "Design",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.design}
          onChange={(e) => onChange(row.id, "design", e.target.value)}
        />
      ),
    },
    {
      id: "size",
      label: "Size",
      width: "40px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.size}
          onChange={(e) => onChange(row.id, "size", e.target.value)}
        />
      ),
    },
    {
      id: "pcs",
      label: "Pcs",
      width: "50px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.pcs}
          onChange={(e) => onChange(row.id, "pcs", e.target.value)}
        />
      ),
    },
    {
      id: "grWt",
      label: (
        <>
          Gr. Wt 
        </>
      ),
      width: "80px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.grWt}
          onChange={(e) => onChange(row.id, "grWt", e.target.value)}
        />
      ),
    },
    {
      id: "lsWt",
      label: "LWt",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.lsWt}
          onChange={(e) => onChange(row.id, "lsWt", e.target.value)}
        />
      ),
    },
    {
      id: "netWt",
      label: "NWt",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.netWt}
          onChange={(e) => onChange(row.id, "netWt", e.target.value)}
        />
      ),
    },
    {
      id: "itRateOn",
      label: "ItRateOn",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.itRateOn}
          onChange={(e) => onChange(row.id, "itRateOn", e.target.value)}
        />
      ),
    },
    {
      id: "itRate",
      label: "ItRate",
      width: "90px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.itRate}
          onChange={(e) => onChange(row.id, "itRate", e.target.value)}
        />
      ),
    },
    {
      id: "itAmt",
      label: "ItAmt",
      width: "90px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.itAmt}
          onChange={(e) => onChange(row.id, "itAmt", e.target.value)}
        />
      ),
    },
    {
      id: "lType",
      label: "Lbr Type",
      width: "40px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.lType}
          onChange={(e) => onChange(row.id, "lType", e.target.value)}
        />
      ),
    },
    {
      id: "lbrPercentage",
      label: "Lbr %",
      width: "70px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.lbrPercentage}
          onChange={(e) => onChange(row.id, "lbrPercentage", e.target.value)}
        />
      ),
    },
    {
      id: "lbrRate",
      label: "Lbr Rate",
      width: "90px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.lbrRate}
          onChange={(e) => onChange(row.id, "lbrRate", e.target.value)}
        />
      ),
    },
    {
      id: "lbrAmt",
      label: "Lbr Amt",
      width: "90px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.lbrAmt}
          onChange={(e) => onChange(row.id, "lbrAmt", e.target.value)}
        />
      ),
    },
    {
      id: "othAmt",
      label: "OthAmt",
      width: "120px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.othAmt}
          onChange={(e) => onChange(row.id, "othAmt", e.target.value)}
        />
      ),
    },
    {
      id: "mrp",
      label: "MRP",
      width: "90px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.mrp}
          onChange={(e) => onChange(row.id, "mrp", e.target.value)}
        />
      ),
    },
    {
      id: "image",
      label: "Image",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="file"
          accept="image/*"
          onChange={(e) => onChange(row.id, "image", e.target.files[0])}
        />
      ),
    },
    {
      id: "totalAmt",
      label: (
        <>
          TotalAmt 
        </>
      ),
      width: "100px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.totalAmt}
          onChange={(e) => onChange(row.id, "totalAmt", e.target.value)}
        />
      ),
    },
  ];

  // Define payment columns structure
  const PAYMENT_COLUMNS = [
    { id: "no", label: "No.", width: "30px", value: (row, index) => index + 1 },
    {
      id: "pay_mode",
      label: "Mode",
      width: "140px",
      value: (row, onChange) => {
        // Get all currently selected methods except the current row's method
        const selectedMethods = paymentRows
          .filter((pr) => pr.id !== row.id)
          .map((pr) => pr.pay_mode);

        // Filter available methods to show only unselected ones and current row's method
        const availableMethods = AVAILABLE_PAYMENT_METHODS.filter(
          (method) =>
            !selectedMethods.includes(method) || method === row.pay_mode
        );

        const options = availableMethods.map((method) => ({
          value: method,
          label: PAYMENT_METHOD_LABELS[method],
        }));

        const selectedOption = options.find(
          (opt) => opt.value === row.pay_mode
        );

        return (
          <Select
            styles={customSelectStyles}
            value={selectedOption || null}
            onChange={(selected) =>
              onChange(row.id, "pay_mode", selected ? selected.value : "")
            }
            options={options}
            placeholder="Select Method"
            menuPortalTarget={document.body}
          />
        );
      },
    },
    {
      id: "type",
      label: "Type",
      width: "140px",
      value: (row, onChange) => {
        // Show available payment types
        const typeOptions = paymentTypes;

        const selectedOption = typeOptions.find(
          (opt) => opt.value === row.type
        );

        return (
          <Select
            styles={customSelectStyles}
            value={selectedOption || null}
            onChange={(selected) =>
              onChange(row.id, "type", selected ? selected.value : "")
            }
            options={typeOptions}
            placeholder="Select Type"
            menuPortalTarget={document.body}
          />
        );
      },
    },

    {
      id: "remarks",
      label: "Remarks",
      width: "180px",
      value: (row, onChange) => (
        <input
          type="text"
          className={`form-control border-0 rounded-0 ${buttonClass}`}
          style={{
            fontSize: "0.85rem",
            padding: "0.4rem 0.5rem",
            height: "100%",
          }}
          // placeholder="Remarks"
          value={row.remarks}
          onChange={(e) => onChange(row.id, "remarks", e.target.value)}
        />
      ),
    },
    {
      id: "amount",
      label: "Amt",
      width: "110px",
      value: (row, onChange, onFocus) => (
        <input
          type="number"
          className={`form-control border-0 rounded-0 ${buttonClass}`}
          style={{
            fontSize: "0.85rem",
            padding: "0.4rem 0.5rem",
            height: "100%",
          }}
          placeholder="0.00"
          value={row.amount}
          onChange={(e) => onChange(row.id, "amount", e.target.value)}
          onFocus={() => onFocus && onFocus(row.id)}
        />
      ),
    },
  ];

  // Function to send tag number to API

  const fetchPaymentTypes = async () => {
    try {
      const response = await listBankAccounts();

      if (response?.success && Array.isArray(response.data?.bank_accounts)) {
        const fetchedBankDetails = response.data.bank_accounts.map((bank) => ({
          id: bank.id,
          bankName: bank.bank_name || "",
          accountHolder: bank.holder_name || "",
          accountType: bank.account_type || "",
          ifscCode: bank.ifsc_code || bank.IFSC_code || "",
          accountNumber: bank.account_number || "",
          openingBalance: bank.opening_balance || "",
          balance:
            bank.available_balance !== null &&
              bank.available_balance !== undefined
              ? bank.account_type === "metal"
                ? `${bank.available_balance} gm`
                : `₹${bank.available_balance}`
              : bank.account_type === "metal"
                ? "0 gm"
                : "₹0",
          status: bank.status === 1 ? "Active" : "Inactive",
          statusClass:
            bank.status === 1
              ? "bg-success-subtle text-success"
              : "bg-danger-subtle text-danger",
          useForInvoice: bank.invoice === 1,
          useForPOS: bank.pos === 1,
        }));

        fetchedBankDetails.sort((a, b) => b.id - a.id);
        setBankAccounts(fetchedBankDetails);

        const paymentOptions = fetchedBankDetails.map((bank) => ({
          value: bank.id,
          label: bank.accountNumber ? `${bank.bankName}` : bank.bankName,
        }));
        setPaymentTypes(paymentOptions);
      } else if (response?.success && !response.data?.bank_accounts?.length) {
        setBankAccounts([]);
        setPaymentTypes([]);
      } else if (response && response.success === false) {
        setBankAccounts([]);
        setPaymentTypes([]);
        toast.error(response.message || "Failed to fetch bank details");
      } else {
        setBankAccounts([]);
        setPaymentTypes([]);
        toast.error("Unexpected response format from server");
      }
    } catch (error) {
      console.error("Error fetching bank details:", error);
      setBankAccounts([]);
      setPaymentTypes([]);
      toast.error(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch bank details"
      );
    }
  };

  useEffect(() => {
    fetchPaymentTypes();
  }, []);

  const fetchKarigars = async (search = "") => {
    const searchText =
      search !== undefined && search !== null ? String(search).trim() : "";

    try {
      const response = await listSuppliers(
        searchText ? { search: searchText } : {}
      );
      const suppliers =
        response?.success && Array.isArray(response.data?.suppliers)
          ? response.data.suppliers
          : [];

      if (Array.isArray(suppliers)) {
        const fetchedKarigars = suppliers.map((karigar) =>
          normalizeKarigar(karigar)
        );

        setKarigars((prevKarigars) =>
          searchText
            ? mergeKarigars(prevKarigars, fetchedKarigars)
            : mergeKarigars([], fetchedKarigars)
        );

        if (searchText) {
          karigarSearchCacheRef.current.add(searchText.toLowerCase());
        }

        return fetchedKarigars;
      }
    } catch (error) {
      console.error("Error fetching karigar details:", error);
      toast.error(error.response?.data?.message || "Failed to fetch karigars");
    }

    return [];
  };

  const [karigars, setKarigars] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);

  // Karigar states
  const [selectedKarigarId, setSelectedKarigarId] = useState(null);
  const [karigarName, setKarigarName] = useState("");
  const [karigarNameSearchText, setKarigarNameSearchText] = useState("");
  const [karigarNameDropdownActive, setKarigarNameDropdownActive] =
    useState(false);
  const [karigarNameHighlightedIndex, setKarigarNameHighlightedIndex] =
    useState(0);
  const karigarNameDropdownRef = useRef(null);
  const karigarNameInputRef = useRef(null);
  const karigarNameItemsRef = useRef([]);

  useEffect(() => {
    if (selectedKarigarId) return;

    const searchText = String(karigarNameSearchText || "").trim();
    if (
      searchText.length < 3 ||
      karigarSearchCacheRef.current.has(searchText.toLowerCase())
    ) {
      return;
    }

    fetchKarigars(searchText);
  }, [karigarNameSearchText, selectedKarigarId]);

  // Demo data for dropdowns
  const [dropdownData, setDropdownData] = useState({
    productGroup: [],
    design: [],
    lType: ["NWt", "GWt", "Pcs", "Fixed"], // Keeping this as it is since it's not from API
    itRateOn: ["NWt", "GWt"], // Adding new options for ItRateOn
  });

  // Columns that should have dropdown functionality
  const dropdownColumns = ["productGroup", "lType", "rateOn", "itRateOn"];

  const initialRowData = COLUMNS.map((col) => ({
    id: col.id,
    value: col.id === "itRateOn" ? "NWt" : "",
    // Add imageFile property for image column
    ...(col.id === "image" ? { imageFile: null, imagePreview: null } : {}),
    ...(col.id === "productGroup" ? { productGroupId: null } : {}),
  }));

  const [tableData, setTableData] = useState([{ id: 1, data: initialRowData }]);
  const [itemFieldErrors, setItemFieldErrors] = useState({});

  useEffect(() => {
    setItemFieldErrors((prev) =>
      pruneLineFieldErrors(
        prev,
        collectLineItemFieldErrors(tableData, CUSTOM_ORDER_LINE_ERROR_OPTIONS),
      ),
    );
  }, [tableData]);

  const [activeDropdown, setActiveDropdown] = useState({
    row: null,
    col: null,
    type: null,
  });
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [lastSearchedProductGroupPrefix, setLastSearchedProductGroupPrefix] =
    useState("");
  const [productGroupApiCalled, setProductGroupApiCalled] = useState(false); // Track if API was called to show "No matching items"
  const [productGroupPoolHeader, setProductGroupPoolHeader] = useState(null);
  const [productGroupSearchLoading, setProductGroupSearchLoading] =
    useState(false);
  const productGroupOptionsCacheRef = useRef([]);
  const dropdownRef = useRef(null);
  const inputRefs = useRef({});
  const tableContainerRef = useRef(null);
  const dropdownItemsRef = useRef([]);
  const fileInputRefs = useRef({});
  const customOrderCreateIdempotencyKeyRef = useRef(null);
  const tooltipRefs = useRef({}); // Add refs for tooltips

  // Add state to track active tooltip
  const [activeTooltip, setActiveTooltip] = useState(null);
  const tooltipTimeoutRef = useRef(null);

  // State for image modal
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageUrl: null,
    rawPath: null,
    zoomLevel: 1,
  });

  // Add state for other amount modal
  const [otherAmountModal, setOtherAmountModal] = useState({
    isOpen: false,
    rowIndex: null,
    entries: [],
    currentEntry: {
      chargeType: "",
      pcs: "",
      ct: "",
      wt: "",
      conPercentage: "",
      rateOn: "",
      rate: "",
      amount: "",
    },
    editingIndex: undefined,
  });

  // Add state to track last Enter keypress time
  const [lastEnterPress, setLastEnterPress] = useState({
    time: 0,
    rowIndex: null,
  });

  // Update dropdown position after API response when dropdown is active
  useEffect(() => {
    if (
      productGroupApiCalled &&
      activeDropdown.type === "productGroup" &&
      activeDropdown.row !== null &&
      activeDropdown.col !== null
    ) {
      setTimeout(() => {
        const inputElement =
          inputRefs.current[`${activeDropdown.row}-${activeDropdown.col}`];
        if (inputElement) {
          const rect = inputElement.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width,
          });
        }
      }, 10);
    }
  }, [productGroupApiCalled, dropdownData.productGroup, activeDropdown]);

  // Update the otherAmountColumnWidths state to use fixed widths
  const [otherAmountColumnWidths, setOtherAmountColumnWidths] = useState({
    no: "10px", // Added for No. column
    chargetype: "150px", // Changed from chargeType to match transformation
    pcs: "20px",
    ct: "50px",
    wt: "60px",
    con: "10px",
    rateon: "50px",
    rate: "68px",
    amount: "80px",
  });

  // Add rateOn options
  const rateOnOptions = ["FIXED", "PCS", "CT", "WT"];

  const [billTypes, setBillTypes] = useState([]);
  const [selectedBillTypeId, setSelectedBillTypeId] = useState("");
  const [selectedBillTypeName, setSelectedBillTypeName] = useState("GST");
  const selectedBillTypeIdRef = useRef("");
  const [billDate, setBillDate] = useState(
    () => new Date().toISOString().split("T")[0],
  );
  // Add expDate state — default to today (same as order date)
  const [expDate, setExpDate] = useState(
    () => new Date().toISOString().split("T")[0],
  );

  // Add customer states
  const [customerMobile, setCustomerMobile] = useState("");
  const [customerMobileSearchText, setCustomerMobileSearchText] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [customerMobileInvalid, setCustomerMobileInvalid] = useState(false);

  const [customerName, setCustomerName] = useState("");
  const [customerNameSearchText, setCustomerNameSearchText] = useState("");

  const [customerAddress, setCustomerAddress] = useState("");
  const [customerCity, setCustomerCity] = useState("");

  useEffect(() => {
    if (selectedCustomerId) return;

    const nextSearch = [customerMobileSearchText, customerNameSearchText]
      .map((value) => (value ? String(value).trim() : ""))
      .find(
        (value) =>
          value.length >= 3 &&
          !customerSearchCacheRef.current.has(value.toLowerCase())
      );

    if (!nextSearch) return;

    fetchUserss(nextSearch);
  }, [customerMobileSearchText, customerNameSearchText, selectedCustomerId]);

  // Add new state for customer details modal
  const [showCustomerDetailsModal, setShowCustomerDetailsModal] =
    useState(false);
  const [customerDetails, setCustomerDetails] = useState({
    email: "",
    birthDate: "",
    anniversaryDate: "",
    companyName: "",
    companyAddress: "",
    gstNumber: "",
    aadharNo: "",
    panNo: "",
    maritalStatus: "",
    balance: "",
    saleBalance: "",
    reference: "",
    state: "",
  });

  const { states, cities, selectedState, setSelectedState } = useIndiaStateCity();

  const [savingCustomer, setSavingCustomer] = useState(false);
  const [customerSaveSuccess, setCustomerSaveSuccess] = useState(false);

  // Add Customer Form States
  // const [fullName, setFullName] = useState("");
  // const [mobile, setMobile] = useState("");
  // const [email, setEmail] = useState("");
  // const [city, setCity] = useState("");
  // const [address, setAddress] = useState("");
  // const [birthDate, setBirthDate] = useState("");
  // const [maritalStatus, setMaritalStatus] = useState("");
  // const [anniversaryDate, setAnniversaryDate] = useState("");
  // const [openingBalance, setOpeningBalance] = useState("");
  // const [pendingBalance, setPendingBalance] = useState("");
  // const [companyName, setCompanyName] = useState("");
  // const [companyAddress, setCompanyAddress] = useState("");
  // const [gstNumber, setGstNumber] = useState("");
  // const [status, setStatus] = useState("active");
  // const [showAddModal, setShowAddModal] = useState(false);

  // Mock customer data - replace with real API data
  const CUSTOMERS = (userss || [])
    .filter((user) => !!user)
    .map((user) => {
      const fullName = user?.fullName || user?.name || "";
      return {
        id: user?.id,
        name: fullName,
        fullName,
        mobile: user?.mobile || "",
        email: user?.email || "",
        city: user?.city || "",
        address: user?.address || "",
        birthDate: user?.birthDate || "",
        maritalStatus: user?.maritalStatus || "",
        anniversaryDate: user?.anniversaryDate || "",
        balance: user?.balance || "",
        saleBalance: user?.saleBalance || "",
        companyName: user?.companyName || "",
        companyAddress: user?.companyAddress || "",
        gstNumber: user?.gstNumber || "",
        aadharNo: user?.aadharNo || "",
        panNo: user?.panNo || "",
        reference: user?.reference || "",
        state: user?.state || "",
      };
    });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    selectedBillTypeIdRef.current = selectedBillTypeId;
  }, [selectedBillTypeId]);

  const applyBillTypeSelection = (billType) => {
    const id = billType?.id != null ? String(billType.id) : "";
    const name = String(billType?.name || "").trim();
    setSelectedBillTypeId(id);
    setSelectedBillTypeName(name || "GST");
  };

  useEffect(() => {
    const fetchBillTypeOptions = async () => {
      try {
        const response = await listBillTypes();
        const rows = Array.isArray(response?.data?.bill_types)
          ? response.data.bill_types
          : [];
        if (!isErpApiSuccess(response) || !Array.isArray(rows)) return;

        const mapped = rows.map((row) => ({
          id: row?.id,
          name: String(row?.name || "").trim(),
        }));
        setBillTypes(mapped);

        if (!selectedBillTypeIdRef.current) {
          const gstType =
            mapped.find((item) => item.name.toLowerCase() === "gst") ||
            mapped[0];
          if (gstType) {
            applyBillTypeSelection(gstType);
          }
        }
      } catch (error) {
        console.error("Error fetching bill types:", error);
      }
    };

    fetchBillTypeOptions();
  }, []);

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setBillDate(today);
    setExpDate(today); // Default Exp Delivery to today
  }, []);

  const handleDateChange = (e) => {
    setBillDate(e.target.value);
  };
  // Add handler for expDate
  const handleExpDateChange = (e) => {
    setExpDate(e.target.value);
  };

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const suggestionTimeoutRef = useRef(null);

  // Update the charge types data to include multiple codes
  const [chargeTypes, ensureOtherCharges] = useLazyOtherChargeOptions();


  // Modify the getSuggestions function to handle multiple codes
  const getSuggestions = (query) => {
    if (queryHasTwoLetters(query)) ensureOtherCharges();
    if (!query || query.length < 1) {
      setSuggestions([]);
      return;
    }

    const searchQuery = query.toLowerCase();

    // First try to match by any code
    let matchedByCode = chargeTypes.filter((charge) =>
      charge.codes.some((code) => code.toLowerCase() === searchQuery)
    );

    // If no exact code match, then try partial matches on codes
    if (matchedByCode.length === 0) {
      matchedByCode = chargeTypes.filter((charge) =>
        charge.codes.some((code) => code.toLowerCase().startsWith(searchQuery))
      );
    }

    // Get matches by name
    const matchedByName = chargeTypes.filter(
      (charge) =>
        charge.name.toLowerCase().includes(searchQuery) &&
        !matchedByCode.some((codeMatch) => codeMatch.name === charge.name)
    );

    // Combine results with code matches first
    const matchedSuggestions = [...matchedByCode, ...matchedByName].map(
      (charge) => `${charge.name} (${charge.codes[0]})`
    );

    setSuggestions(matchedSuggestions);
    setShowSuggestions(true);
    setSelectedSuggestionIndex(0);
  };

  // Add function to handle suggestion selection
  const handleSuggestionSelect = (suggestion, rowIndex = null) => {
    // Extract just the name without the code in brackets
    const chargeName = suggestion.split(" (")[0];

    if (rowIndex !== null) {
      // For existing entries
      const newEntries = [...otherAmountModal.entries];
      newEntries[rowIndex].chargeType = chargeName;
      setOtherAmountModal((prev) => ({
        ...prev,
        entries: newEntries,
      }));
    } else {
      // For current entry
      setOtherAmountModal((prev) => ({
        ...prev,
        currentEntry: {
          ...prev.currentEntry,
          chargeType: chargeName,
        },
      }));
    }
    setShowSuggestions(false);
  };

  // Reset highlighted index when dropdown opens or filter changes
  useEffect(() => {
    if (activeDropdown.row !== null && activeDropdown.col !== null) {
      setHighlightedIndex(0);
      dropdownItemsRef.current = [];
    }
  }, [activeDropdown, tableData]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (dropdownRef.current && dropdownItemsRef.current[highlightedIndex]) {
      dropdownItemsRef.current[highlightedIndex].scrollIntoView({
        block: "nearest",
      });
    }
  }, [highlightedIndex]);

  // Sync pool balance when navigating product group dropdown
  useEffect(() => {
    if (
      activeDropdown.type !== "productGroup" ||
      activeDropdown.row === null ||
      activeDropdown.col === null
    ) {
      return;
    }
    const cell = tableData[activeDropdown.row]?.data[activeDropdown.col];
    const filtered = getFilteredItems(cell?.value, "productGroup");
    if (filtered[highlightedIndex]) {
      applyPoolHeaderFromGroup(filtered[highlightedIndex]);
    }
  }, [highlightedIndex, activeDropdown, dropdownData.productGroup]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown({ row: null, col: null, type: null });
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Create portal container for dropdown
  useEffect(() => {
    setupPortalContainers();
    return teardownPortalContainers;
  }, []);

  // Add this function to calculate Net Weight
  const calculateNetWeight = (gw, lw) => {
    return gw - lw; // NWt = GWt - LWt
  };

  // Function to calculate Labor Amount using Lbr % (based on itRate per gram)
  const calculateLbrAmtWithPercentage = (
    baseWeight,
    lbrPercentage,
    itRatePerGram
  ) => {
    return (
      (lbrPercentage / 100) * baseWeight * (parseFloat(itRatePerGram) || 0)
    );
  };

  // Function to calculate Labor Amount using Lbr Rate
  const calculateLbrAmtWithRate = (baseWeight, lbrRate) => {
    return lbrRate * baseWeight;
  };

  // Update the handleInputChange function to calculate Lbr Amt
  const handleInputChange = (rowIndex, colIndex, value) => {
    let newTableData = [...tableData];
    const cell = newTableData[rowIndex].data[colIndex];
    const id = cell.id;
    // If user edits itRate manually, mark override and recalc itAmt
    if (id === "itRate") {
      cell.value = value;
      newTableData[rowIndex].userOverrodeItRate = true;
      const itRate = parseFloat(value) || 0;
      const itRateOnCell = newTableData[rowIndex].data.find(
        (c) => c.id === "itRateOn"
      );
      const netWtCell = newTableData[rowIndex].data.find(
        (c) => c.id === "netWt"
      );
      const grWtCell = newTableData[rowIndex].data.find((c) => c.id === "grWt");
      const itAmtCell = newTableData[rowIndex].data.find(
        (c) => c.id === "itAmt"
      );
      if (itAmtCell && (netWtCell || grWtCell)) {
        const itRateOn = itRateOnCell?.value || "NWt";
        const netWt = parseFloat(netWtCell?.value) || 0;
        const grWt = parseFloat(grWtCell?.value) || 0;
        const baseWeight = itRateOn === "GWt" ? grWt : netWt;
        itAmtCell.value = (baseWeight * itRate).toFixed(2);
        calculateTotalAmount(newTableData, rowIndex);
      }
      setTableData(newTableData);
      return;
    }

    // Store previous value to check if it has changed
    const previousValue = cell.value;

    // If the changed column is lbrType
    if (id === "lbrType") {
      cell.value = value;

      // If Fixed is selected, clear lbrPercentage and lbrRate
      if (value === "Fixed") {
        const lbrPercentageCell = newTableData[rowIndex].data.find(
          (cell) => cell.id === "lbrPercentage"
        );
        const lbrRateCell = newTableData[rowIndex].data.find(
          (cell) => cell.id === "lbrRate"
        );

        if (lbrPercentageCell) {
          lbrPercentageCell.value = "";
        }
        if (lbrRateCell) {
          lbrRateCell.value = "";
        }
      }

      setTableData(newTableData);
      return;
    }

    // Check if value matches the validation pattern for this column
    const validationPatterns = {
      design: /^[a-zA-Z0-9\s\-\_]*$/, // Allow alphanumeric, spaces, hyphens, underscores
      size: /^\d*$/, // Only numbers
      pcs: /^\d*$/, // Only numbers
      lbrPercentage: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      lbrRate: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      lbrAmt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      othAmt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      mrp: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      grWt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      lsWt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      netWt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      itRate: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      itAmt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      totalAmt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
    };

    // If pattern exists and value doesn't match, don't update
    if (validationPatterns[id] && !validationPatterns[id].test(value)) {
      // console.log(`Validation failed for ${id}: ${value}`);
      return; // Don't update if validation fails
    }

    // Handle productGroup: fetch options when user types 3+ characters
    if (id === "productGroup") {
      cell.value = value;

      const matchingProductGroup = getProductGroupOptionsList().find(
        (pg) =>
          pg.label === value ||
          pg.name === value ||
          pg.short_code === value,
      );
      cell.productGroupId = matchingProductGroup
        ? matchingProductGroup.value
        : null;

      setTableData(newTableData);

      // Trigger API call when user types 3+ characters
      if (value && value.length >= 3) {
        // Only reset if the new value doesn't start with the last searched prefix
        if (
          !lastSearchedProductGroupPrefix ||
          value !== lastSearchedProductGroupPrefix
        ) {
          setProductGroupApiCalled(false); // Reset flag before API call
          fetchProductGroupByPrefix(value);
        }
      } else {
        setDropdownData((prev) => ({ ...prev, productGroup: [] }));
        setLastSearchedProductGroupPrefix("");
        setProductGroupApiCalled(false);
        setProductGroupPoolHeader(null);
      }
      return;
    }

    cell.value = value;

    // Handle calculations based on the changed field
    if (id === "grWt" || id === "lsWt") {
      // Calculate Net Weight when Gross Weight or Less Weight changes
      const gwCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "grWt"
      );
      const lwCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "lsWt"
      );

      if (gwCell && lwCell) {
        const grWt = parseFloat(gwCell.value) || 0;
        const lsWt = parseFloat(lwCell.value) || 0;
        const netWt = calculateNetWeight(grWt, lsWt);

        // Update the netWt cell
        const netWtCell = newTableData[rowIndex].data.find(
          (cell) => cell.id === "netWt"
        );
        if (netWtCell) {
          netWtCell.value = netWt.toString();

          // If itRate exists, recalculate itAmt based on itRateOn
          const itRateCell = newTableData[rowIndex].data.find(
            (cell) => cell.id === "itRate"
          );
          const itRateOnCell = newTableData[rowIndex].data.find(
            (cell) => cell.id === "itRateOn"
          );

          if (itRateCell && itRateCell.value) {
            const itRate = parseFloat(itRateCell.value) || 0;
            if (itRateOnCell && !itRateOnCell.value) {
              itRateOnCell.value = "NWt";
            }
            const itRateOn = itRateOnCell?.value || "NWt";

            // Determine which weight to use based on itRateOn
            const baseWeight = itRateOn === "GWt" ? grWt : netWt;

            const itAmt = (baseWeight * itRate).toFixed(2);

            // Update the itAmt cell
            const itAmtCell = newTableData[rowIndex].data.find(
              (cell) => cell.id === "itAmt"
            );
            if (itAmtCell) {
              itAmtCell.value = itAmt.toString();

              // Recalculate totalAmt
              calculateTotalAmount(newTableData, rowIndex);
            }
          }
        }
      }
    } else if (id === "netWt" || id === "grWt" || id === "itRateOn") {
      // Recalculate itRate first (to ensure it's up to date), then calculate itAmt
      const updatedData = calculateAndUpdateItRate(newTableData, rowIndex);
      newTableData = updatedData;
    } else if (id === "lType") {
      // Handle LBR type change
      const lTypeCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "lType"
      );
      if (lTypeCell) {
        lTypeCell.value = value;
      }
    } else if (id === "lbrAmt" || id === "othAmt" || id === "itAmt") {
      // Recalculate totalAmt when any of the amount fields change
      // But only recalculate totalAmt, don't trigger labor calculations
      calculateTotalAmount(newTableData, rowIndex);
    }

    // Handle LBR percentage and rate calculations
    if (id === "lType" || id === "lbrPercentage" || id === "lbrRate") {
      // Track which labor field was last changed
      if (id === "lbrPercentage" || id === "lbrRate") {
        newTableData[rowIndex].lastChangedLaborField = id;
      }
      handleLaborCalculations(newTableData, rowIndex);
    }

    setTableData(newTableData);
  };

  // Helper to calculate and update ItRate and ItAmt for a row using live rate
  const calculateAndUpdateItRate = (tableDataArg, rowIndex) => {
    let newTableDataLocal = [...tableDataArg];
    const productGroupCell = newTableDataLocal[rowIndex].data.find(
      (cell) => cell.id === "productGroup"
    );

    if (
      !productGroupCell ||
      (!productGroupCell.value &&
        (productGroupCell.productGroupId == null ||
          productGroupCell.productGroupId === ""))
    ) {
      return newTableDataLocal;
    }

    const selectedProductGroup =
      (productGroupCell.productGroupId != null &&
        productGroupCell.productGroupId !== "" &&
        getProductGroupOptionsList().find(
          (pg) =>
            String(pg.value) === String(productGroupCell.productGroupId),
        )) ||
      getProductGroupOptionsList().find(
        (pg) =>
          pg.label === productGroupCell.value ||
          pg.name === productGroupCell.value ||
          pg.short_code === productGroupCell.value,
      );

    if (selectedProductGroup) {
      let effectiveRate = 0;
      if (selectedProductGroup.metal === "Gold") {
        if (Array.isArray(masterGoldRate)) {
          const label = (selectedProductGroup.label || "").toLowerCase();
          const karatMatch = label.match(/(\d+)c/);
          const karat = karatMatch ? parseInt(karatMatch[1]) : 24;
          const indexMap = { 24: 0, 22: 1, 18: 2 };
          let idx = indexMap[karat] ?? 0;
          if (label.includes("rose")) {
            idx = 3;
          }
          effectiveRate = Number(masterGoldRate[idx] || 0);
        } else {
          if (selectedProductGroup.purity) {
            effectiveRate = calculatePurityBasedRate(
              selectedProductGroup.purity,
              Number(masterGoldRate || 0),
              "Gold"
            );
          } else {
            effectiveRate = calculatePerGramRate(
              Number(masterGoldRate || 0),
              "Gold"
            );
          }
        }
      } else if (selectedProductGroup.metal === "Silver") {
        if (selectedProductGroup.purity) {
          effectiveRate = calculatePurityBasedRate(
            selectedProductGroup.purity,
            Number(masterSilverRate || 0),
            "Silver"
          );
        } else {
          effectiveRate = calculatePerGramRate(
            Number(masterSilverRate || 0),
            "Silver"
          );
        }
      } else {
        const baseRate =
          selectedProductGroup.metal === "Gold"
            ? masterGoldRate
            : masterSilverRate;
        effectiveRate = calculatePerGramRate(
          Number(baseRate || 0),
          selectedProductGroup.metal
        );
      }

      const itRateCell = newTableDataLocal[rowIndex].data.find(
        (cell) => cell.id === "itRate"
      );
      if (itRateCell) {
        const isOverridden = newTableDataLocal[rowIndex].userOverrodeItRate;
        const finalRate = isOverridden
          ? parseFloat(itRateCell.value) || 0
          : effectiveRate;
        if (!isOverridden) {
          itRateCell.value = Number(effectiveRate || 0).toFixed(2);
        }

        const itRateOnCell = newTableDataLocal[rowIndex].data.find(
          (cell) => cell.id === "itRateOn"
        );
        const netWtCell = newTableDataLocal[rowIndex].data.find(
          (cell) => cell.id === "netWt"
        );
        const grWtCell = newTableDataLocal[rowIndex].data.find(
          (cell) => cell.id === "grWt"
        );

        if (itRateOnCell && (netWtCell || grWtCell)) {
          if (!itRateOnCell.value) {
            itRateOnCell.value = "NWt";
          }
          const itRateOn = itRateOnCell.value || "NWt";
          const netWt = parseFloat(netWtCell?.value) || 0;
          const grWt = parseFloat(grWtCell?.value) || 0;
          let baseWeight = itRateOn === "GWt" ? grWt : netWt;

          const itAmt = (baseWeight * finalRate).toFixed(2);
          const itAmtCell = newTableDataLocal[rowIndex].data.find(
            (cell) => cell.id === "itAmt"
          );
          if (itAmtCell) {
            itAmtCell.value = itAmt.toString();
          }
          calculateTotalAmount(newTableDataLocal, rowIndex);
        }
      }
    }

    return newTableDataLocal;
  };

  // New function to calculate total amount
  const calculateTotalAmount = (tableData, rowIndex) => {
    const itAmtCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "itAmt"
    );
    const lbrAmtCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "lbrAmt"
    );
    const othAmtCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "othAmt"
    );

    const itAmt = parseFloat(itAmtCell?.value) || 0;
    const lbrAmt = parseFloat(lbrAmtCell?.value) || 0;
    const othAmt = parseFloat(othAmtCell?.value) || 0;

    const totalAmt = (itAmt + lbrAmt + othAmt).toFixed(2);

    // Update the totalAmt cell
    const totalAmtCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "totalAmt"
    );
    if (totalAmtCell) {
      totalAmtCell.value = totalAmt.toString();
    }
  };

  // Handle image upload
  const handleImageUpload = async (field, rowIndex) => {
    // console.log("Field:", field);

    // Create a preview of the image
    const fileReader = new FileReader();
    fileReader.onload = async (e) => {
      // Update the image preview and store the file in the table data
      setTableData((prevData) => {
        const updatedData = [...prevData];
        if (updatedData[rowIndex] && updatedData[rowIndex].data) {
          const imageCell = updatedData[rowIndex].data.find(
            (cell) => cell.id === "image"
          );
          if (imageCell) {
            imageCell.imagePreview = e.target.result;
            imageCell.imageFile = field; // Store the actual file object
            imageCell.value = null; // Clear any previous uploaded image name
          }
        }
        return updatedData;
      });
    };
    fileReader.readAsDataURL(field);
  };

  // Open image modal
  const openImageModal = (imageUrl, rawPath = "") => {
    // Clear any active tooltips when opening modal
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    setActiveTooltip(null);

    // Hide any visible tooltips
    Object.values(tooltipRefs.current).forEach((tooltip) => {
      if (tooltip) tooltip.style.display = "none";
    });

    setImageModal({
      isOpen: true,
      imageUrl,
      rawPath: rawPath || imageUrl,
      zoomLevel: 1,
    });
  };

  // Close image modal
  const closeImageModal = () => {
    setImageModal({
      isOpen: false,
      imageUrl: null,
      rawPath: null,
      zoomLevel: 1,
    });
  };

  // Render image modal
  const renderImageModal = () => {
    if (!imageModal.isOpen) return null;

    return ReactDOM.createPortal(
      <Modal show={imageModal.isOpen} onHide={closeImageModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Image Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <img
              src={imageModal.imageUrl}
              alt="Preview"
              data-raw-path={imageModal.rawPath || ""}
              data-candidate-index="0"
              onError={handleProductImageError}
              style={{
                maxWidth: "100%",
                maxHeight: "400px",
                objectFit: "contain",
              }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                // Trigger file input to change the image
                const rowIndex = tableData.findIndex((row) =>
                  row.data.some(
                    (cell) =>
                      cell.id === "image" &&
                      cell.imagePreview === imageModal.imageUrl
                  )
                );

                if (rowIndex !== -1) {
                  const colIndex = tableData[rowIndex].data.findIndex(
                    (cell) => cell.id === "image"
                  );
                  if (colIndex !== -1) {
                    triggerFileInput(rowIndex, colIndex);
                    closeImageModal();
                  }
                }
              }}
              title="Change Image"
            >
              <Icon icon="solar:gallery-edit-bold" width="16" height="16" />
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                // Find and remove the image
                const rowIndex = tableData.findIndex((row) =>
                  row.data.some(
                    (cell) =>
                      cell.id === "image" &&
                      cell.imagePreview === imageModal.imageUrl
                  )
                );

                if (rowIndex !== -1) {
                  const colIndex = tableData[rowIndex].data.findIndex(
                    (cell) => cell.id === "image"
                  );
                  if (colIndex !== -1) {
                    setTableData((prevData) => {
                      const updatedData = [...prevData];
                      const imageCell = updatedData[rowIndex].data.find(
                        (c) => c.id === "image"
                      );
                      if (imageCell) {
                        imageCell.value = "";
                        imageCell.imageFile = null;
                        imageCell.imagePreview = null;
                      }
                      return updatedData;
                    });
                    closeImageModal();
                  }
                }
              }}
              title="Delete Image"
            >
              <Icon icon="solar:trash-bin-trash-bold" width="16" height="16" />
            </Button>
          </div>
        </Modal.Footer>
      </Modal>,
      document.getElementById("modal-portal")
    );
  };

  const handleAddRow = () => {
    // Flag to prevent multiple simultaneous calls
    if (window.isAddingRow) return;
    window.isAddingRow = true;

    // Get the current length before adding
    const newRowIndex = tableData.length;

    setTableData((prevData) => [
      ...prevData,
      {
        id: prevData.length + 1,
        data: COLUMNS.map((col) => ({
          id: col.id,
          value: col.id === "itRateOn" ? "NWt" : "",
          // Add imageFile property for image column
          ...(col.id === "image"
            ? { imageFile: null, imagePreview: null }
            : {}),
        })),
      },
    ]);

    // Keep new row + Total in view; allow adding again after a short delay
    setTimeout(() => {
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTop =
          tableContainerRef.current.scrollHeight;
      }
      window.isAddingRow = false;
    }, 150);
  };

  // Function to focus on the first cell of the next row
  const focusFirstCellOfNextRow = (currentRowIndex) => {
    setTimeout(() => {
      const nextRowIndex = currentRowIndex + 1;
      if (nextRowIndex < tableData.length) {
        const firstCellInput = inputRefs.current[`${nextRowIndex}-0`];
        if (firstCellInput) firstCellInput.focus();
      }
    }, 0);
  };

  const handleDropdownClick = (rowIndex, colIndex, columnType) => {
    // Get position of the input element for positioning the dropdown
    const inputElement = inputRefs.current[`${rowIndex}-${colIndex}`];
    if (inputElement) {
      const rect = inputElement.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }

    setActiveDropdown({ row: rowIndex, col: colIndex, type: columnType });
    setHighlightedIndex(0);
  };

  const handleDropdownSelect = (rowIndex, colIndex, selectedItem) => {
    // Ensure we're passing the label or value, not the entire object
    const value =
      typeof selectedItem === "object" ? selectedItem.label : selectedItem;

    const columnId = COLUMNS[colIndex]?.id;

    // Keep productGroupId when selecting from dropdown (queue hydrate / manual pick)
    if (columnId === "productGroup") {
      setTableData((prev) => {
        const copy = [...prev];
        const cell = copy[rowIndex]?.data[colIndex];
        if (cell) {
          cell.productGroupId =
            typeof selectedItem === "object" ? selectedItem.value : null;
          cell.value = value;
        }
        return copy;
      });
    } else {
      handleInputChange(rowIndex, colIndex, value);
    }
    setActiveDropdown({ row: null, col: null, type: null });

    if (columnId === "productGroup" && typeof selectedItem === "object") {
      productGroupOptionsCacheRef.current = mergeProductGroupOptions(
        productGroupOptionsCacheRef.current,
        [selectedItem],
      );
      applyPoolHeaderFromGroup(selectedItem);
    }

    // Calculate purity-based rate when product group is selected
    if (columnId === "productGroup") {
      setTableData((prev) => calculateAndUpdateItRate(prev, rowIndex));
    }

    // Focus on the next cell after selection
    setTimeout(() => {
      const nextColIndex = colIndex + 1;
      if (nextColIndex < COLUMNS.length) {
        const nextInput = inputRefs.current[`${rowIndex}-${nextColIndex}`];
        if (nextInput) nextInput.focus();
      } else {
        // If it's the last cell in the row, focus on the first cell of the next row
        focusFirstCellOfNextRow(rowIndex);
      }
    }, 0);
  };

  // Get filtered items based on input value and column type
  const getFilteredItems = (searchText, columnType) => {
    // console.log("Column Type:", columnType);
    // console.log("Dropdown Data:", dropdownData);
    // console.log("Search Text:", searchText);

    if (!dropdownData[columnType]) return [];

    // Normalize search text
    const normalizedSearchText = searchText
      ? String(searchText).toLowerCase()
      : "";

    // If no search, return all
    if (!normalizedSearchText) return dropdownData[columnType];

    // Special case: array of strings
    if (columnType === "lType" || columnType === "itRateOn") {
      return dropdownData[columnType].filter((item) =>
        item.toLowerCase().includes(normalizedSearchText)
      );
    }

    // Special case: productGroup — API search results (no client-side re-filter)
    if (columnType === "productGroup") {
      return dropdownData[columnType] || [];
    }

    // Default handling
    return dropdownData[columnType].filter((item) => {
      if (typeof item === "object") {
        return item.label
          ? item.label.toLowerCase().includes(normalizedSearchText)
          : false;
      }
      return item.toLowerCase().includes(normalizedSearchText);
    });
  };

  // Add this function to get filtered items for Rate On dropdown
  const getFilteredRateOnItems = (searchText) => {
    if (!searchText) return rateOnOptions;
    return rateOnOptions.filter((item) =>
      item.toLowerCase().includes(searchText.toLowerCase())
    );
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, rowIndex, colIndex, columnType) => {
    // Handle Tab key to move to next cell and potentially add a new row
    if (e.key === "Tab" && !e.shiftKey) {
      // If we're at the last column of the last row
      if (
        colIndex === COLUMNS.length - 1 &&
        rowIndex === tableData.length - 1
      ) {
        e.preventDefault();
        handleAddRow();
        // Focus will be set to the first cell of the new row after render
        setTimeout(() => {
          const newRowIndex = tableData.length;
          const firstCellInput = inputRefs.current[`${newRowIndex}-0`];
          if (firstCellInput) firstCellInput.focus();
        }, 0);
      }
    }

    // Handle dropdown keyboard navigation
    if (columnType === "rateOn" || dropdownColumns.includes(columnType)) {
      const cell = tableData[rowIndex]?.data[colIndex];
      if (!cell) return;

      const filtered =
        columnType === "rateOn"
          ? getFilteredRateOnItems(cell.value)
          : getFilteredItems(cell.value, columnType);

      if (filtered.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < filtered.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
          break;
        case "Enter":
          e.preventDefault();
          if (filtered[highlightedIndex]) {
            handleDropdownSelect(
              rowIndex,
              colIndex,
              filtered[highlightedIndex]
            );
          }
          break;
        case "Escape":
          e.preventDefault();
          setActiveDropdown({ row: null, col: null, type: null });
          break;
        case "Tab":
          setActiveDropdown({ row: null, col: null, type: null });
          break;
        default:
          break;
      }
    }
  };

  // Render dropdown through portal
  const renderDropdown = () => {
    if (
      activeDropdown.row === null ||
      activeDropdown.col === null ||
      !activeDropdown.type
    )
      return null;

    const cell = tableData[activeDropdown.row]?.data[activeDropdown.col];
    if (!cell) return null;

    const filtered = getFilteredItems(cell.value, activeDropdown.type);
    // Reset refs array
    dropdownItemsRef.current = [];

    // Calculate max width based on content
    const contentWidths = filtered.map((item) => {
      const tempSpan = document.createElement("span");
      tempSpan.style.fontSize = "0.85rem";
      tempSpan.style.visibility = "hidden";
      tempSpan.style.position = "absolute";
      tempSpan.style.whiteSpace = "nowrap";

      // Safely get the label
      const itemLabel = typeof item === "object" ? item.label || "" : item;
      tempSpan.textContent = itemLabel;

      document.body.appendChild(tempSpan);
      const width = tempSpan.offsetWidth;
      document.body.removeChild(tempSpan);
      return width;
    });

    const maxContentWidth =
      contentWidths.length > 0
        ? Math.max(...contentWidths)
        : dropdownPosition.width;

    // Special styling for productGroup dropdown - match AddGirvi.jsx pattern
    const isProductGroup = activeDropdown.type === "productGroup";
    // For productGroup: use 5 items with 40px height each (matching AddGirvi.jsx)
    // For others: use 5 items with 35px height each
    const VISIBLE_COUNT = 5;
    const ITEM_HEIGHT = isProductGroup ? 40 : 35;
    const visibleItems = Math.min(filtered.length, VISIBLE_COUNT);
    const dropdownHeight = visibleItems * ITEM_HEIGHT;

    // Calculate width for dropdowns - match AddGirvi.jsx pattern
    const adjustedWidth = isProductGroup
      ? Math.max(200, dropdownPosition.width)
      : Math.max(dropdownPosition.width, maxContentWidth + 20);

    return ReactDOM.createPortal(
      <div
        ref={dropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "absolute",
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          width: `${adjustedWidth}px`,
          minWidth: `${dropdownPosition.width}px`,
          maxWidth: `${adjustedWidth}px`,
          maxHeight: isProductGroup ? `${dropdownHeight + 16}px` : `${dropdownHeight}px`,
          height: isProductGroup ? `${dropdownHeight + 16}px` : `${dropdownHeight}px`,
          overflow: "hidden",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          padding: "0.5rem 0",
          backgroundColor: "#fff",
          boxSizing: "border-box",
          boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
        }}
      >
        <div
          style={{
            maxHeight: `${dropdownHeight}px`,
            height: `${dropdownHeight}px`,
            overflowY: filtered.length > VISIBLE_COUNT ? "auto" : "hidden",
            overflowX: "hidden",
          }}
        >
          {isProductGroup && productGroupSearchLoading ? (
            <div
              className="dropdown-item text-muted"
              style={{ cursor: "default", padding: "0.4rem 0.75rem" }}
            >
              Searching...
            </div>
          ) : filtered.length > 0 ? (
            filtered.map((item, idx) => {
              // Handle both object and string types
              const itemLabel =
                typeof item === "object"
                  ? isProductGroup
                    ? pgDropdownLabel(item)
                    : item.label || ""
                  : item;
              const isSelected =
                typeof item === "object"
                  ? (item.label || "").toLowerCase() ===
                  (cell.value || "").toLowerCase()
                  : item.toLowerCase() === (cell.value || "").toLowerCase();

              return (
                <div
                  key={idx}
                  ref={(el) => (dropdownItemsRef.current[idx] = el)}
                  className={`dropdown-item ${idx === highlightedIndex ? "active" : ""
                    }`}
                  title={itemLabel}
                  style={{
                    cursor: "pointer",
                    padding: "0.4rem 0.75rem",
                    fontSize: "0.85rem",
                    backgroundColor:
                      idx === highlightedIndex
                        ? "var(--theme-primary)"
                        : isSelected
                          ? "#f0f0f0"
                          : "transparent",
                    color: idx === highlightedIndex ? "#fff" : "inherit",
                    whiteSpace: isProductGroup ? "normal" : "nowrap",
                    wordWrap: isProductGroup ? "break-word" : "normal",
                    overflow: isProductGroup ? "visible" : "hidden",
                    textOverflow: isProductGroup ? "clip" : "ellipsis",
                  }}
                  onMouseEnter={() => {
                    setHighlightedIndex(idx);
                    if (isProductGroup) applyPoolHeaderFromGroup(item);
                  }}
                  onMouseDown={() =>
                    handleDropdownSelect(
                      activeDropdown.row,
                      activeDropdown.col,
                      item
                    )
                  }
                >
                  {itemLabel}
                </div>
              );
            })
          ) : (
            <div
              className="dropdown-item disabled"
              style={{
                padding: "0.4rem 0.75rem",
                fontSize: "0.85rem",
                textAlign: "center",
              }}
            >
              No matching items
            </div>
          )}
        </div>
      </div>,
      document.getElementById("dropdown-portal")
    );
  };

  // Check if a column should have dropdown functionality
  const isDropdownColumn = (columnId) => {
    return dropdownColumns.includes(columnId);
  };

  // Check if a column is the image column
  const isImageColumn = (columnId) => {
    return columnId === "image";
  };

  // Get column type from index
  const getColumnType = (colIndex) => {
    return COLUMNS[colIndex]?.id;
  };

  // Add a function to handle focus on the last column
  const handleFocus = (rowIndex, colIndex) => {
    // Change background color
    const inputElement = inputRefs.current[`${rowIndex}-${colIndex}`];
    if (inputElement) {
      inputElement.style.backgroundColor = "#fff";
    }

    // If this is the last column of the last row, add a new row
    if (colIndex === COLUMNS.length - 1 && rowIndex === tableData.length - 1) {
      handleAddRow();
    }

    // If this is a dropdown column, show the dropdown
    // For productGroup, only show dropdown if user has typed 3+ characters and we have results
    const columnType = getColumnType(colIndex);
    if (isDropdownColumn(columnType)) {
      if (columnType === "productGroup") {
        // For productGroup, only show dropdown if user has typed 3+ characters AND we have results or API was called
        const productGroupCell = tableData[rowIndex]?.data?.find(
          (cell) => cell.id === "productGroup"
        );
        const productGroupValue = (productGroupCell?.value || "")
          .toString()
          .trim();
        const hasEnoughChars = productGroupValue.length >= 3;
        if (!hasEnoughChars && productGroupValue) {
          const selected = getProductGroupOptionsList().find(
            (pg) =>
              pg.name === productGroupValue || pg.label === productGroupValue,
          );
          applyPoolHeaderFromGroup(selected);
        }
        const hasResults =
          getFilteredItems(productGroupValue, "productGroup").length > 0;

        // Only show dropdown if user has typed 3+ characters AND (has results OR API was called)
        if (hasEnoughChars && (hasResults || productGroupApiCalled)) {
          handleDropdownClick(rowIndex, colIndex, columnType);
        }
        // Don't show dropdown if user just clicked/focused without typing 3+ characters
      } else {
        // For other dropdown columns, show dropdown normally
        handleDropdownClick(rowIndex, colIndex, columnType);
      }
    }
  };

  // Trigger file input click
  const triggerFileInput = (rowIndex, colIndex) => {
    const fileInput = fileInputRefs.current[`${rowIndex}-${colIndex}`];
    if (fileInput) {
      fileInput.click();
    }
  };

  // Modify the handleOtherAmountInteraction function
  const handleOtherAmountInteraction = (rowIndex, event) => {
    if (event.type === "dblclick") {
      event.preventDefault();
      openOtherAmountModal(rowIndex);
    } else if (event.type === "keydown" && event.key === "Enter") {
      const now = Date.now();
      if (
        lastEnterPress.rowIndex === rowIndex &&
        now - lastEnterPress.time < 500
      ) {
        // 500ms threshold for double-Enter
        event.preventDefault();
        openOtherAmountModal(rowIndex);
        setLastEnterPress({ time: 0, rowIndex: null });
      } else {
        setLastEnterPress({ time: now, rowIndex });
      }
    } else if (event.type === "click") {
      // Show tooltip for 4 seconds on click
      const tooltipId = `oth-amt-tooltip-${rowIndex}`;
      const tooltip = tooltipRefs.current[tooltipId];

      if (tooltip) {
        // Clear any existing timeout
        if (tooltipTimeoutRef.current) {
          clearTimeout(tooltipTimeoutRef.current);
        }

        // Check if tooltip is already visible
        if (tooltip.style.display === "block") {
          // Reset the timeout to keep it visible
          tooltipTimeoutRef.current = setTimeout(() => {
            tooltip.style.display = "none";
            setActiveTooltip(null);
          }, 4000);
          return; // Exit to prevent further actions
        }

        tooltip.style.display = "block"; // Show tooltip immediately
        setActiveTooltip(tooltipId); // Set active tooltip

        // Set timeout to hide after 4 seconds
        tooltipTimeoutRef.current = setTimeout(() => {
          tooltip.style.display = "none";
          setActiveTooltip(null);
        }, 4000);
      }
    }
  };

  // Add helper function to open modal
  const openOtherAmountModal = (rowIndex) => {
    const currentEntries =
      tableData[rowIndex].data.find((cell) => cell.id === "othAmt")
        .otherAmountEntries || [];
    const currentValue =
      tableData[rowIndex].data.find((cell) => cell.id === "othAmt").value || "";

    // If there's a direct value but no entries, create an initial entry
    const initialEntries =
      currentEntries.length > 0
        ? currentEntries.map((entry) => ({
          ...entry,
          rateOn: entry.rateOn || "", // Ensure rateOn is initialized
        }))
        : currentValue
          ? [
            {
              chargeType: "Direct Entry",
              pcs: "",
              ct: "",
              wt: "",
              conPercentage: "",
              rateOn: "",
              rate: "",
              amount: currentValue,
            },
          ]
          : [];

    setOtherAmountModal({
      isOpen: true,
      rowIndex,
      entries: [...initialEntries],
      currentEntry: {
        chargeType: "",
        pcs: "",
        ct: "",
        wt: "",
        conPercentage: "",
        rateOn: "",
        rate: "",
        amount: "",
      },
      editingIndex: undefined,
    });
  };

  // Add function to save other amount entries
  const handleSaveOtherAmountEntries = () => {
    const { rowIndex, entries, currentEntry } = otherAmountModal;

    // If there's data in the current entry, validate and add it to entries
    let finalEntries = [...entries];
    if (currentEntry.amount && currentEntry.chargeType) {
      if (isNaN(currentEntry.amount)) {
        alert("Please enter a valid amount");
        return;
      }
      finalEntries.push({ ...currentEntry });
    }

    // Calculate total amount
    const totalAmount = finalEntries.reduce(
      (sum, entry) => sum + (parseFloat(entry.amount) || 0),
      0
    );

    // Calculate total less weight
    const totalLessWeight = finalEntries.reduce((sum, entry) => {
      const lessWeight = calculateLessWeight(entry);
      return sum + (isNaN(lessWeight) ? 0 : lessWeight);
    }, 0);

    // Update table data with both values
    setTableData((prev) => {
      const newData = [...prev];
      const row = newData[rowIndex];

      if (!row || !row.data) return prev;

      // Get all required values
      const grWtCell = row.data.find((cell) => cell.id === "grWt");
      const lTypeCell = row.data.find((cell) => cell.id === "lType");
      const lbrPercentageCell = row.data.find(
        (cell) => cell.id === "lbrPercentage"
      );
      const lbrRateCell = row.data.find((cell) => cell.id === "lbrRate");
      const itRateCell = row.data.find((cell) => cell.id === "itRate");
      const itRateOnCell = row.data.find((cell) => cell.id === "itRateOn");
      const productGroupCell = row.data.find(
        (cell) => cell.id === "productGroup"
      );

      const grWt = parseFloat(grWtCell?.value) || 0;
      const lType = lTypeCell?.value;
      const lbrPercentage = parseFloat(lbrPercentageCell?.value) || 0;
      const lbrRate = parseFloat(lbrRateCell?.value) || 0;
      const itRate = parseFloat(itRateCell?.value) || 0;
      if (itRateOnCell && !itRateOnCell.value) {
        itRateOnCell.value = "NWt";
      }
      const itRateOn = itRateOnCell?.value || "NWt";

      // Get metal type from product group
      const selectedProductGroup = getProductGroupOptionsList().find(
        (pg) => pg.label === productGroupCell?.value
      );
      const metalType = selectedProductGroup?.metal || "Gold";

      // Update Other Amount
      const othAmtCell = row.data.find((cell) => cell.id === "othAmt");
      if (othAmtCell) {
        othAmtCell.value = formatAmount(totalAmount);
        othAmtCell.otherAmountEntries = finalEntries;
      }

      const lsWtCell = row.data.find((cell) => cell.id === "lsWt");
      const currentLsWtVal = parseFloat(lsWtCell?.value) || 0;
      const newLsWtVal = formatLessWeight(totalLessWeight);
      if (lsWtCell && totalLessWeight > 0) {
        lsWtCell.value = newLsWtVal;
      }

      const lwForCalc = totalLessWeight > 0 ? totalLessWeight : currentLsWtVal;
      const netWeight = calculateNetWeight(grWt, lwForCalc);
      const netWtCell = row.data.find((cell) => cell.id === "netWt");
      if (netWtCell) {
        netWtCell.value = netWeight.toFixed(2);
      }

      // Calculate Item Amount based on ItRateOn
      const itAmtCell = row.data.find((cell) => cell.id === "itAmt");
      if (itRateCell && itRate && itAmtCell) {
        const baseWeight = itRateOn === "GWt" ? grWt : netWeight;
        const itAmt = (baseWeight * itRate).toFixed(2);
        itAmtCell.value = itAmt;
      }

      // Calculate Labor Amount
      const lbrAmtCell = row.data.find((cell) => cell.id === "lbrAmt");
      if (lbrAmtCell) {
        let baseWeight = 0;
        if (lType === "NWt") {
          baseWeight = netWeight;
        } else if (lType === "GWt") {
          baseWeight = grWt;
        }

        let lbrAmt = 0;
        const itRatePerGram = parseFloat(itRateCell?.value) || 0;
        if (lbrPercentage > 0) {
          lbrAmt = calculateLbrAmtWithPercentage(
            baseWeight,
            lbrPercentage,
            itRatePerGram
          );
        } else if (lbrRate > 0) {
          lbrAmt = calculateLbrAmtWithRate(baseWeight, lbrRate);
        }
        lbrAmtCell.value = lbrAmt.toFixed(2);
      }

      // Calculate Total Amount
      const itAmt = parseFloat(itAmtCell?.value) || 0;
      const lbrAmt = parseFloat(lbrAmtCell?.value) || 0;
      const othAmt = parseFloat(othAmtCell?.value) || 0;
      const totalAmt = (itAmt + lbrAmt + othAmt).toFixed(2);

      const totalAmtCell = row.data.find((cell) => cell.id === "totalAmt");
      if (totalAmtCell) {
        totalAmtCell.value = totalAmt;
      }

      return newData;
    });

    // Close modal after saving
    setOtherAmountModal((prev) => ({
      ...prev,
      isOpen: false,
    }));
  };

  // Add function to handle column width changes
  const handleOtherAmountColumnWidthChange = (column, newWidth) => {
    setOtherAmountColumnWidths((prev) => ({
      ...prev,
      [column]: `${newWidth}px`,
    }));
  };

  // Add this function right before the return statement in the Table component
  // Function to position tooltip based on screen size
  const positionTooltip = (targetElement, tooltip) => {
    const rect = targetElement.getBoundingClientRect();
    tooltip.style.display = "block";

    // Get arrow element
    const arrow = tooltip.querySelector(".tooltip-arrow");

    // Responsive positioning based on viewport
    const viewportWidth = window.innerWidth;

    if (viewportWidth < 576) {
      // Small screens (Bootstrap's sm breakpoint)
      // Position directly below the "i" button
      tooltip.style.left = `${rect.left + rect.width / 2 - 100}px`; // Center horizontally (tooltip width is 200px)
      tooltip.style.top = `${rect.bottom + 10}px`; // Position below the button
      tooltip.style.maxWidth = "calc(100vw - 20px)"; // 10px padding on each side

      // Position arrow at top center
      if (arrow) {
        arrow.style.top = "-6px"; // Adjusted for better alignment
        arrow.style.left = "50%";
        arrow.style.transform = "translateX(-50%) rotate(45deg)";
        arrow.style.borderRight = "none";
        arrow.style.borderBottom = "none";
        arrow.style.borderLeft = "1px solid rgba(0,0,0,0.1)";
        arrow.style.borderTop = "1px solid rgba(0,0,0,0.1)";
      }
    } else {
      // Position to the right on larger screens
      tooltip.style.left = `${rect.right + 10}px`;
      tooltip.style.top = `${rect.top - 5}px`;
      tooltip.style.maxWidth = "300px";

      // Position arrow at left middle
      if (arrow) {
        arrow.style.top = "15px";
        arrow.style.left = "-6px";
        arrow.style.transform = "rotate(45deg)";
        arrow.style.borderRight = "none";
        arrow.style.borderBottom = "none";
      }
    }
  };

  // Update the Rate On dropdown implementation
  const handleRateOnDropdown = (rowIndex, value, isCurrentEntry = false) => {
    if (isCurrentEntry) {
      setOtherAmountModal((prev) => {
        const updatedEntry = {
          ...prev.currentEntry,
          rateOn: value,
        };
        // Calculate amount when rate on changes
        if (updatedEntry.rate) {
          updatedEntry.amount = formatAmount(calculateAmount(updatedEntry));
        }
        return {
          ...prev,
          currentEntry: updatedEntry,
        };
      });
    } else {
      setOtherAmountModal((prev) => {
        const newEntries = [...prev.entries];
        const updatedEntry = {
          ...newEntries[rowIndex],
          rateOn: value,
        };
        // Calculate amount when rate on changes
        if (updatedEntry.rate) {
          updatedEntry.amount = formatAmount(calculateAmount(updatedEntry));
        }
        newEntries[rowIndex] = updatedEntry;
        return {
          ...prev,
          entries: newEntries,
        };
      });
    }

    // Hide the dropdown after selection
    const dropdowns = document.querySelectorAll(".dropdown-menu");
    dropdowns.forEach((dropdown) => {
      if (dropdown.style.display === "block") {
        dropdown.style.display = "none";
      }
    });
  };

  // Add keyboard navigation for Rate On dropdown
  const handleRateOnKeyDown = (e, rowIndex, isCurrentEntry = false) => {
    const currentValue = isCurrentEntry
      ? otherAmountModal.currentEntry.rateOn
      : otherAmountModal.entries[rowIndex]?.rateOn;

    const filtered = rateOnOptions.filter((option) =>
      option.toLowerCase().includes((currentValue || "").toLowerCase()),
    );

    const dropdown = e.target.nextElementSibling;

    if (e.key === "Enter") {
      e.preventDefault();
      if (
        dropdown &&
        dropdown.style.display === "block" &&
        filtered.length > 0 &&
        filtered[highlightedIndex]
      ) {
        handleRateOnDropdown(
          rowIndex,
          filtered[highlightedIndex],
          isCurrentEntry,
        );
        setTimeout(() => {
          const row = e.target.closest("tr");
          const rateInput = row?.querySelectorAll("td")[7]?.querySelector("input");
          rateInput?.focus();
        }, 0);
      } else if (dropdown) {
        dropdown.style.display = "block";
        setHighlightedIndex(0);
      }
      return;
    }

    if (filtered.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (dropdown && dropdown.style.display !== "block") {
          dropdown.style.display = "block";
        }
        setHighlightedIndex((prev) =>
          prev < filtered.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        if (dropdown && dropdown.style.display !== "block") {
          dropdown.style.display = "block";
        }
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case "Escape":
        e.preventDefault();
        if (dropdown) {
          dropdown.style.display = "none";
        }
        break;
      default:
        break;
    }
  };


  const getRowEffectiveAmount = (row) => {
    if (!row || !row.data) return 0;

    const mrpCell = row.data.find((cell) => cell.id === "mrp");
    const mrpValue = mrpCell ? parseFloat(mrpCell.value) : NaN;
    const hasMrp =
      mrpCell && mrpCell.value !== "" && !isNaN(mrpValue) && mrpValue > 0;

    if (hasMrp) {
      return mrpValue;
    }

    const totalAmtCell = row.data.find((cell) => cell.id === "totalAmt");
    const totalAmtValue = totalAmtCell ? parseFloat(totalAmtCell.value) : NaN;

    return isNaN(totalAmtValue) ? 0 : totalAmtValue;
  };

  const calculateRawTableTotal = () => {
    if (!tableData) return 0;
    return tableData.reduce((sum, row) => {
      const effectiveAmount = getRowEffectiveAmount(row);
      return sum + effectiveAmount;
    }, 0);
  };

  const tableTotalAutoRound = useMemo(() => {
    return autoRoundOff(calculateRawTableTotal());
  }, [tableData]);

  // Matches the main table TotalAmt footer (auto round-off applied), same as Sales.
  const calculateTotal = () => tableTotalAutoRound.rounded;

  // Add a helper function for number formatting
  const formatAmount = (value) => {
    if (!value || isNaN(value)) return "";
    // Convert to number and handle rounding based on decimal part
    const num = parseFloat(value);
    const decimalPart = num - Math.floor(num);
    // If decimal part is >= 0.5, round up, otherwise round down
    return decimalPart >= 0.5
      ? Math.ceil(num).toString()
      : Math.floor(num).toString();
  };

  // Update formatLessWeight to always return a value with 2 decimal places
  const formatLessWeight = (value) => {
    // Convert to number first
    const num = parseFloat(value);
    // Check if it's a valid number (including 0)
    if (isNaN(num)) return "0.00";
    // Always return with 2 decimal places
    return num.toFixed(2);
  };

  // Add this function to calculate Labor Amount
  const calculateLbrAmt = (
    baseWeight,
    lbrPercentage,
    lbrRate,
    itRatePerGram
  ) => {
    let lbrAmt = 0;
    if (lbrPercentage > 0) {
      lbrAmt = calculateLbrAmtWithPercentage(
        baseWeight,
        lbrPercentage,
        itRatePerGram
      );
    } else if (lbrRate > 0) {
      lbrAmt = calculateLbrAmtWithRate(baseWeight, lbrRate);
    }
    return lbrAmt;
  };


  // Calculate GST amount based on percentage of Final Amount
  const calculateGstAmount = () => {
    const finalAmount = calculateTotal();
    const gstPercentValue = parseFloat(gstPercent) || 0;
    const gstAmt = (finalAmount * gstPercentValue) / 100;
    return gstAmt;
  };

  // Amount before payment-section round-off (Final + money-rounded GST).
  const calculateAmountBeforeRoundOff = () => {
    const finalAmount = calculateTotal();
    const gstAmt = Math.round((calculateGstAmount() || 0) * 100) / 100;
    return finalAmount + gstAmt;
  };

  const getAutoPaymentRoundOff = () =>
    autoRoundOff(calculateAmountBeforeRoundOff()).roundOff;

  const getEffectiveRoundOff = () => {
    if (isManualRoundOff) {
      return parseFloat(roundOffAmount) || 0;
    }
    return getAutoPaymentRoundOff();
  };

  const getDisplayRoundOff = () => {
    if (isManualRoundOff) {
      return roundOffAmount;
    }
    return getAutoPaymentRoundOff().toFixed(2);
  };

  // Calculate grand total (Final Amount + GST + round-off adjustment)
  const calculateGrandTotal = () => {
    const amountBeforeRoundOff = calculateAmountBeforeRoundOff();
    const roundOff = getEffectiveRoundOff();
    return (amountBeforeRoundOff + roundOff).toFixed(2);
  };

  // Define product and setProductGroups using useState

  const [design, setDesign] = useState(""); // Initialize design state
  const [size, setSize] = useState(""); // Initialize size state
  const [pcs, setPcs] = useState(0); // Initialize pcs state
  const [grWt, setGrWt] = useState(0); // Initialize grWt state
  const [lsWt, setLsWt] = useState(0); // Initialize lsWt state
  const [netWt, setNetWt] = useState(0); // Initialize netWt state
  const [lType, setLType] = useState(""); // Initialize lType state
  const [lbrPercentage, setLbrPercentage] = useState(0); // Initialize lbrPercentage state
  const [lbrRate, setLbrRate] = useState(0); // Initialize lbrRate state
  const [lbrAmt, setLbrAmt] = useState(0); // Initialize lbramt state
  const [othamt, setOthAmt] = useState(0); // Initialize othAmt state
  const [mrp, setMrp] = useState(0); // Initialize mrp state
  const [image, setImage] = useState(null); // Initialize image state
  const [No, setNo] = useState(0); // Initialize No state (assuming it's a number)
  const [productGroup, setProductGroup] = useState(""); // Initialize productGroup state (assuming it's a string)
  const [showSuccessModal, setShowSuccessModal] = useState(false); // State for modal visibility
  const [roundOffAmount, setRoundOffAmount] = useState(""); // Change to string for input
  const [isManualRoundOff, setIsManualRoundOff] = useState(false);
  const [gstPercent, setGstPercent] = useState("3"); // GST percentage
  const [gstAmount, setGstAmount] = useState("0.00"); // GST amount
  const [note, setNote] = useState(""); // Add note state


  // Add resetForm function before handleSubmit
  const resetForm = () => {
    // Reset bill type and date
    setBillDate(new Date().toISOString().split("T")[0]);

    // Reset customer fields
    setCustomerMobile("");
    setCustomerMobileSearchText("");
    setCustomerName("");
    setCustomerNameSearchText("");
    setCustomerAddress("");
    setCustomerCity("");
    setSelectedCustomerId(null);
    setCustomerDetails({
      email: "",
      birthDate: "",
      anniversaryDate: "",
      companyName: "",
      companyAddress: "",
      gstNumber: "",
      aadharNo: "",
      panNo: "",
      maritalStatus: "",
      balance: "",
      saleBalance: "",
      reference: "",
      state: "",
    });

    // Reset table data
    setTableData([{ id: 1, data: initialRowData }]);
    setItemFieldErrors({});

    // Reset payment rows
    setPaymentRows([
      {
        id: 1,
        pay_mode: "cash", // Pre-selected cash
        type: "",
        remarks: "",
        amount: "",
      },
      {
        id: 2,
        pay_mode: "cheque", // Pre-selected cheque
        type: "",
        remarks: "",
        amount: "",
      },
      {
        id: 3,
        pay_mode: "bank_transfer", // Pre-selected bank transfer
        type: "",
        remarks: "",
        amount: "",
      },
    ]);


    // Reset other fields
    setRoundOffAmount("");
    setIsManualRoundOff(false);
    setGstPercent("3");
    setGstAmount("0.00");
    setNote("");
    setFollowUpDate("");
    setPayableRows([
      {
        id: 1,
        pay_mode: "cash",
        type: "",
        remarks: "",
        amount: "",
      },
    ]);
    setExpDate(new Date().toISOString().split("T")[0]); // Reset Exp Delivery to today
    setExpDueDate(""); // Reset global Exp Date
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const itemErrors = collectLineItemFieldErrors(
        tableData,
        CUSTOM_ORDER_LINE_ERROR_OPTIONS,
      );
      const hasItemErrors = Object.keys(itemErrors).length > 0;
      setItemFieldErrors(hasItemErrors ? itemErrors : {});

      // Get customer ID from the selected customer
      const mobileToSearch = customerMobileSearchText || customerMobile;

      const emailError = getEmailValidationError(customerDetails?.email);
      if (emailError) {
        toast.error(emailError);
        return;
      }

      const nameToUse = customerNameSearchText || customerName;

      let selectedCustomer = userss.find(
        (c) => String(c.mobile).trim() === String(mobileToSearch).trim()
      );

      // Prefer already-picked customer id when present
      if (
        !selectedCustomer &&
        selectedCustomerId != null &&
        selectedCustomerId !== "" &&
        !String(selectedCustomerId).startsWith("TEMP_")
      ) {
        selectedCustomer = userss.find(
          (c) => String(c.id) === String(selectedCustomerId),
        );
      }

      // If customer not found in existing list, check if we have enough details to create one
      if (!selectedCustomer && mobileToSearch && nameToUse) {
        // Create a temporary user object from the form state
        const tempId = `TEMP_${Date.now()}`;
        selectedCustomer = {
          id: tempId,
          mobile: mobileToSearch,
          fullName: nameToUse,
          name: nameToUse,
          email: customerDetails.email || "",
          state: customerDetails.state || "Gujarat",
          area: customerDetails.area || "",
          city: customerCity || "Ahmedabad",
          address: customerAddress || "",
          aadharNo: customerDetails.aadharNo || "",
          panNo: customerDetails.panNo || "",
          birthDate: customerDetails.birthDate || "",
          maritalStatus: customerDetails.maritalStatus || "",
          anniversaryDate: customerDetails.anniversaryDate || "",
          balance: customerDetails.balance || "",
          saleBalance: customerDetails.saleBalance || "",
          companyName: customerDetails.companyName || "",
          companyAddress: customerDetails.companyAddress || "",
          gstNumber: customerDetails.gstNumber || "",
          reference: customerDetails.reference || "",
        };
      }

      if (!selectedCustomer) {
        toast.error("Please select a valid customer");
        return;
      }

      // Check if this is a new customer (temp ID)
      const isNewCustomer =
        selectedCustomer.id &&
        String(selectedCustomer.id).toString().startsWith("TEMP_");

      if (isNewCustomer && !isValidMobileNumber(mobileToSearch)) {
        setCustomerMobileInvalid(true);
        toast.error("Enter valid number");
        return;
      }
      setCustomerMobileInvalid(false);

      let newUserData = [];
      let userIdToSend = selectedCustomer.id;

      if (isNewCustomer) {
        userIdToSend = null;
      }

      // Populate newUserData only if it's a new customer OR if customer details were updated
      if (isNewCustomer || customerSaveSuccess) {
        newUserData = [
          {
            name: selectedCustomer.name || selectedCustomer.fullName,
            mobile: selectedCustomer.mobile,
            email: selectedCustomer.email,
            city: selectedCustomer.city,
            address: selectedCustomer.address,
            aadhar_no: selectedCustomer.aadharNo,
            pan_no: selectedCustomer.panNo,
            birth_date: selectedCustomer.birthDate || "0000-00-00",
            marital_status: selectedCustomer.maritalStatus,
            anniversary_date: selectedCustomer.anniversaryDate || "0000-00-00",
            balance: selectedCustomer.balance,
            sale_balance: selectedCustomer.saleBalance,
            company_name: selectedCustomer.companyName,
            company_address: selectedCustomer.companyAddress,
            GST_number: selectedCustomer.gstNumber,
            reference: selectedCustomer.reference,
            state: selectedCustomer.state,
            area: selectedCustomer.area || "",
            status: "1",
          },
        ];
      }

      // Get logged-in user ID
      const legacyUser =
        (typeof getUserData === "function" ? getUserData() : null) || {};
      const erpUser = getErpAuthState?.()?.user || {};
      const by_user =
        legacyUser?.id ??
        legacyUser?.ua_id ??
        erpUser?.id ??
        erpUser?.ua_id ??
        erpUser?.user_id;
      if (!by_user) {
        toast.error("User not logged in");
        return;
      }

      // Ensure exp_delivery uses expDate and exp_date uses expDueDate
      const formattedExpDelivery = expDate
        ? new Date(expDate).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0];

      const formattedExpDate = expDueDate
        ? new Date(expDueDate).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0];

      if (hasItemErrors) {
        return;
      }

      // --- Begin AddStock-style row processing ---
      const customOrderRows = [];
      const formData = new FormData();

      for (let rowIndex = 0; rowIndex < tableData.length; rowIndex++) {
        const rowData = tableData[rowIndex].data.reduce((acc, cell) => {
          acc[cell.id] = cell.value;
          return acc;
        }, {});

        const productGroupCell = tableData[rowIndex].data.find(
          (cell) => cell.id === "productGroup",
        );
        const hasProductGroup =
          (rowData.productGroup &&
            String(rowData.productGroup).trim() !== "") ||
          (productGroupCell?.productGroupId != null &&
            productGroupCell.productGroupId !== "" &&
            String(productGroupCell.productGroupId) !== "0");

        // Skip rows with empty required fields (productGroup)
        if (!hasProductGroup) {
          // Optionally log: console.log(`Skipping row ${rowIndex + 1} due to missing required fields`);
          continue;
        }

        // Prefer productGroupId from queue hydrate / dropdown select
        const selectedProductGroup =
          (productGroupCell?.productGroupId != null &&
            productGroupCell.productGroupId !== "" &&
            getProductGroupOptionsList().find(
              (group) =>
                String(group.value) === String(productGroupCell.productGroupId),
            )) ||
          getProductGroupOptionsList().find(
            (group) =>
              group.label === rowData.productGroup ||
              group.short_code === rowData.productGroup,
          );

        const productGroupValue =
          selectedProductGroup?.value ??
          productGroupCell?.productGroupId ??
          null;

        if (
          productGroupValue == null ||
          productGroupValue === "" ||
          String(productGroupValue) === "0"
        ) {
          // Optionally log: console.error("Product Group not found:", rowData.productGroup);
          continue;
        }

        // Get other amount entries (charges)
        const otherAmtCell = tableData[rowIndex].data.find(
          (cell) => cell.id === "othAmt"
        );
        const otherAmountEntries = otherAmtCell?.otherAmountEntries || [];

        const charges = mapOtherAmountEntriesForCreate(otherAmountEntries);

        // Get LBR amount
        let lbrAmount = 0;
        const lbrAmtCell = tableData[rowIndex].data.find(
          (cell) => cell.id === "lbrAmt"
        );
        if (lbrAmtCell && lbrAmtCell.value) {
          lbrAmount = Number(lbrAmtCell.value) || 0;
        }

        // Find the image file for this row
        const imageCell = tableData[rowIndex].data.find(
          (cell) => cell.id === "image"
        );
        let imageName = "";
        if (imageCell && imageCell.imageFile) {
          imageName = imageCell.imageFile.name; // Only the original file name
        }

        // Create custom order row in the format the API expects
        const tag_no = `ORD${String(rowIndex + 1).padStart(3, "0")}`;
        customOrderRows.push({
          tag_no,
          karigar_id: null,
          exp_date: formattedExpDate,
          product_group: productGroupValue,
          design: rowData.design || "",
          size: rowData.size || "",
          pcs: Number(rowData.pcs) || 0,
          gwt: rowData.grWt || "",
          lwt: rowData.lsWt || "",
          nwt: rowData.netWt || "",
          itrateon: rowData.itRateOn || "",
          itrate: rowData.itRate || "",
          itamt: rowData.itAmt || "",
          lbr_type: rowData.lType || "",
          lbr_percent: rowData.lbrPercentage || "",
          lbr_rate: rowData.lbrRate || "",
          lbr_amt: lbrAmount,
          othamt: rowData.othAmt || "",
          mrp: rowData.mrp || "",
          totalamt: rowData.totalAmt || "",
          image: imageName,
          image_name: imageName,
          charges,
        });
      }

      // Add image files to FormData after processing all rows (like AddStock.jsx)
      for (let rowIndex = 0; rowIndex < tableData.length; rowIndex++) {
        const imageCell = tableData[rowIndex].data.find(
          (cell) => cell.id === "image"
        );
        if (imageCell && imageCell.imageFile) {
          formData.append(
            `image_${rowIndex}`,
            imageCell.imageFile,
            imageCell.imageFile.name
          );
        }
      }

      // Check if we have any valid entries
      if (customOrderRows.length === 0) {
        alert(
          "No valid product entries to submit. Please complete at least one row with Product Group and Design."
        );
        return;
      }

      // Align item totalamt with Final Amt (table footer auto round-off), same as Sales.
      {
        const rawItemsTotal = customOrderRows.reduce((sum, it) => {
          const mrp = Number(it.mrp) || 0;
          const totalAmt = Number(it.totalamt) || 0;
          return sum + (mrp > 0 ? mrp : totalAmt);
        }, 0);
        const { rounded: roundedItemsTotal, roundOff: itemsRoundOff } =
          autoRoundOff(rawItemsTotal);
        if (itemsRoundOff !== 0) {
          const lastIdx = customOrderRows.length - 1;
          const last = customOrderRows[lastIdx];
          const lastMrp = Number(last.mrp) || 0;
          const othersSum = customOrderRows
            .slice(0, lastIdx)
            .reduce((sum, it) => {
              const mrp = Number(it.mrp) || 0;
              const totalAmt = Number(it.totalamt) || 0;
              return sum + (mrp > 0 ? mrp : totalAmt);
            }, 0);
          const adjusted =
            Math.round((roundedItemsTotal - othersSum) * 100) / 100;
          if (lastMrp > 0) {
            customOrderRows[lastIdx].mrp = String(adjusted);
          } else {
            customOrderRows[lastIdx].totalamt = String(adjusted);
          }
        }
      }

      // --- End AddStock-style row processing ---

      const paymentPersistKeys = [];
      const customOrderPayments = buildStandardPaymentLines(paymentRows, {
        isUpdate: false,
        paymentTypes,
        payModeLabels: PAYMENT_METHOD_LABELS,
        createKey: createCustomOrderIdempotencyKey,
        persistKeys: paymentPersistKeys,
      });

      if (paymentPersistKeys.length > 0) {
        setPaymentRows((prev) => {
          const next = [...prev];
          paymentPersistKeys.forEach(({ rowId, key }) => {
            const idx = next.findIndex((r) => r.id === rowId);
            if (idx >= 0) {
              next[idx] = { ...next[idx], paymentIdempotencyKey: key };
            }
          });
          return next;
        });
      }

      const grandTotal = Number(calculateGrandTotal()) || 0;
      const taxAmount = Number(gstAmount) || 0;
      const discountAmount = 0;

      if (!customOrderCreateIdempotencyKeyRef.current) {
        customOrderCreateIdempotencyKeyRef.current =
          typeof crypto !== "undefined" &&
            typeof crypto.randomUUID === "function"
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      }

      // `createCustomOrderMultipartForm()` already prepends `type=create`.
      formData.append("idempotency_key", customOrderCreateIdempotencyKeyRef.current);
      formData.append("customer_id", String(userIdToSend));
      formData.append("date", billDate || new Date().toISOString().split("T")[0]);
      formData.append(
        "bill_type_id",
        selectedBillTypeId ? String(Number(selectedBillTypeId)) : "",
      );
      formData.append("bill_type_name", selectedBillTypeName || "");
      formData.append(
        "estimate",
        String(
          String(selectedBillTypeName || "").toLowerCase() === "quotation"
            ? 1
            : 0,
        ),
      );
      formData.append("expected_delivery", formattedExpDelivery);
      formData.append("discount", String(discountAmount));
      formData.append("tax_amount", String(taxAmount));
      formData.append("round_off", String(getEffectiveRoundOff()));
      formData.append("grand_total", String(grandTotal));
      formData.append("notes", note || "");
      if (followUpDate) {
        const formattedFollowupDate = new Date(followUpDate)
          .toISOString()
          .split("T")[0];
        formData.append("followup_date", formattedFollowupDate);
      }

      // Keep legacy/new-customer + queue context fields for compatibility.
      formData.append("new_user", JSON.stringify(newUserData));
      formData.append("queue_details_id", queueDetailsId || null);
      formData.append("queued_by_user", customOrderData?.by_user || null);
      formData.append("queue_by_user", customOrderData?.by_user || null);
      formData.append("items", JSON.stringify(customOrderRows));
      formData.append("payments", JSON.stringify(customOrderPayments));
      // console.log("Form Data Entries:", formData);

      for (let [key, val] of formData.entries()) {
        // console.log(key, val);
      }

      // Ensure the queue entry is still available (prevents duplicates)
      if (queueDetailsId) {
        try {
          const queueEntry =
            findOrderQueueInResponse(
              await showOrderQueue({ id: queueDetailsId }),
              queueDetailsId,
            ) ||
            findOrderQueueInResponse(
              await listOrderQueue({
                search: String(queueDetailsId),
                page: 1,
                limit: 20,
              }),
              queueDetailsId,
            );
          if (
            queueEntry &&
            (!isZeroOrNullValue(queueEntry.sale_id) ||
              !isZeroOrNullValue(queueEntry.custom_order_id) ||
              !isZeroOrNullValue(queueEntry.repairing_id))
          ) {
            toast.error("This order was already processed by another user");
            navigate("/orderqueue");
            return;
          }
        } catch (verifyErr) {
          console.warn("Queue verification failed", verifyErr);
        }
      }

      const data = await createCustomOrderMultipartForm(formData);

      if (isErpApiSuccess(data) || data?.message === "radha") {
        toast.success("Order submitted successfully");
        customOrderCreateIdempotencyKeyRef.current = null;
        setShowSuccessModal(true);
        setTimeout(() => {
          setShowSuccessModal(false);
          resetForm();
          navigate("/customorderentrytable");
        }, 800);
      } else {
        toast.error(data?.message || "Operation failed");
      }
    } catch (error) {
      console.error("Submit Error:", error);
      customOrderCreateIdempotencyKeyRef.current = null;
      toast.error(
        error.response?.data?.message ||
        error.message ||
        "Failed to submit the form."
      );
    }
  };

  // New function to handle labor calculations
  const handleLaborCalculations = (tableData, rowIndex) => {
    const lTypeCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "lType"
    );
    const lbrPercentageCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "lbrPercentage"
    );
    const lbrRateCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "lbrRate"
    );
    const lbrAmtCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "lbrAmt"
    );
    const productGroupCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "productGroup"
    );

    const lType = lTypeCell?.value || "";
    const lbrPercentage = parseFloat(lbrPercentageCell?.value) || 0;
    const lbrRate = parseFloat(lbrRateCell?.value) || 0;
    const itRateCellRef = tableData[rowIndex].data.find(
      (cell) => cell.id === "itRate"
    );
    const itRatePerGram = parseFloat(itRateCellRef?.value) || 0;

    // If the labor type is Fixed, clear the percentage and rate fields
    if (lType === "Fixed") {
      if (lbrPercentageCell) lbrPercentageCell.value = "";
      if (lbrRateCell) lbrRateCell.value = "";
      return; // No need to calculate anything for Fixed type
    }

    // Determine the base weight based on labor type
    let baseWeight = 0;
    if (lType === "NWt") {
      const netWtCell = tableData[rowIndex].data.find(
        (cell) => cell.id === "netWt"
      );
      baseWeight = parseFloat(netWtCell?.value) || 0;
    } else if (lType === "GWt") {
      const grWtCell = tableData[rowIndex].data.find(
        (cell) => cell.id === "grWt"
      );
      baseWeight = parseFloat(grWtCell?.value) || 0;
    } else if (lType === "Pcs") {
      const pcsCell = tableData[rowIndex].data.find(
        (cell) => cell.id === "pcs"
      );
      baseWeight = parseFloat(pcsCell?.value) || 0;
    }

    // Calculate labor amount based on which field was last changed
    let lbrAmt = 0;
    const lastChangedField = tableData[rowIndex].lastChangedLaborField;

    if (lastChangedField === "lbrPercentage" && lbrPercentage > 0) {
      lbrAmt = calculateLbrAmtWithPercentage(
        baseWeight,
        lbrPercentage,
        itRatePerGram
      );
      // Update labor rate based on percentage of itRate
      if (lbrRateCell) {
        lbrRateCell.value = ((lbrPercentage / 100) * itRatePerGram).toFixed(2);
      }
    } else if (lastChangedField === "lbrRate" && lbrRate > 0) {
      lbrAmt = calculateLbrAmtWithRate(baseWeight, lbrRate);
      // Update labor percentage based on rate
      if (lbrPercentageCell) {
        lbrPercentageCell.value = itRatePerGram
          ? ((lbrRate / itRatePerGram) * 100).toFixed(2)
          : "";
      }
    } else if (!lastChangedField) {
      // If no field was changed yet (e.g., when changing lType), prefer percentage if available
      if (lbrPercentage > 0) {
        lbrAmt = calculateLbrAmtWithPercentage(
          baseWeight,
          lbrPercentage,
          itRatePerGram
        );
      } else if (lbrRate > 0) {
        lbrAmt = calculateLbrAmtWithRate(baseWeight, lbrRate);
      }
    }

    // Update the labor amount cell
    if (lbrAmtCell) {
      lbrAmtCell.value = Math.round(lbrAmt).toString();
      // Recalculate total amount
      calculateTotalAmount(tableData, rowIndex);
    }
  };

  // Get filtered customers based on mobile number

  // Handle customer name input change
  // Close karigar dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        karigarNameDropdownRef.current &&
        !karigarNameDropdownRef.current.contains(event.target) &&
        karigarNameInputRef.current &&
        !karigarNameInputRef.current.contains(event.target)
      ) {
        setKarigarNameDropdownActive(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [paymentTypes, setPaymentTypes] = useState([]);

  const [isPaymentDropdownOpen, setIsPaymentDropdownOpen] = useState(false);
  const paymentDropdownRef = useRef(null);

  // Initialize payment rows state with three common payment methods
  const [paymentRows, setPaymentRows] = useState([
    {
      id: 1,
      pay_mode: "cash", // Pre-selected cash
      type: "",
      remarks: "",
      amount: "",
    },
    {
      id: 2,
      pay_mode: "cheque", // Pre-selected cheque
      type: "",
      remarks: "",
      amount: "",
    },
    {
      id: 3,
      pay_mode: "bank_transfer", // Pre-selected bank transfer
      type: "",
      remarks: "",
      amount: "",
    },
  ]);

  const [payableRows, setPayableRows] = useState([
    {
      id: 1,
      pay_mode: "cash",
      type: "",
      remarks: "",
      amount: "",
    },
  ]);

  const AVAILABLE_PAYMENT_METHODS = [
    "cash",
    "cheque",
    "bank_transfer",
    "upi",
    "card",
    "old_gold",
    "old_silver",
    "kasar",
    "scheme",
    "order",
    "os",
    "wallet", // Added wallet
  ];


  const getNextAvailableMethod = () => {
    const usedMethods = paymentRows.map((row) => row.pay_mode);
    return AVAILABLE_PAYMENT_METHODS.find(
      (method) => !usedMethods.includes(method)
    );
  };

  const addPaymentRow = () => {
    const newRow = {
      id: paymentRows.length + 1,
      pay_mode: "",
      type: "",
      remarks: "",
      amount: "",
    };
    setPaymentRows([...paymentRows, newRow]);
  };

  // Function to add a new payment row
  const handlePaymentRowChange = (rowId, field, value) => {
    // Auto-select type when Mode is "cash", "wallet", "kasar", "old_gold" or "old_silver"
    if (
      field === "pay_mode" &&
      ["cash", "wallet", "kasar", "old_gold", "old_silver"].includes(value)
    ) {
      let autoSelectedType = null;

      const findAccount = (keyword) =>
        bankAccounts.find(
          (b) =>
            (b.accountType &&
              (b.accountType.toLowerCase() === keyword ||
                b.accountType.toLowerCase() === keyword.replace("_", " "))) ||
            (b.bankName &&
              b.bankName.toLowerCase().includes(keyword.replace("_", " ")))
        );

      const account = findAccount(value);
      if (account) autoSelectedType = account.id;

      const updatedRows = paymentRows.map((row) =>
        row.id === rowId
          ? {
            ...row,
            [field]: value,
            type: autoSelectedType || "",
          }
          : row
      );
      setPaymentRows(updatedRows);
      return;
    }

    const updatedRows = paymentRows.map((row) => {
      if (row.id === rowId) {
        return { ...row, [field]: value };
      }
      return row;
    });
    setPaymentRows(updatedRows);
  };

  // Auto-select payment types for initial rows once bank accounts are loaded
  useEffect(() => {
    if (bankAccounts.length > 0 && paymentRows.length > 0) {
      let hasChanges = false;
      const updatedRows = paymentRows.map((row) => {
        if (
          !row.type &&
          ["cash", "wallet", "kasar", "old_gold", "old_silver"].includes(
            row.pay_mode
          )
        ) {
          const findAccount = (keyword) =>
            bankAccounts.find(
              (b) =>
                (b.accountType &&
                  (b.accountType.toLowerCase() === keyword ||
                    b.accountType.toLowerCase() ===
                    keyword.replace("_", " "))) ||
                (b.bankName &&
                  b.bankName.toLowerCase().includes(keyword.replace("_", " ")))
            );

          const account = findAccount(row.pay_mode);
          if (account) {
            hasChanges = true;
            return { ...row, type: account.id };
          }
        }
        return row;
      });

      if (hasChanges) {
        setPaymentRows(updatedRows);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankAccounts]);

  // Function to handle focus on amount field
  const handleAmountFieldFocus = (rowId) => {
    // Check if this is the last row in the list
    if (rowId === paymentRows[paymentRows.length - 1].id) {
      // Add a new row when the amount field of the last row is focused
      const nextMethod = getNextAvailableMethod();
      if (nextMethod) {
        // Only add if there's an available payment method
        addPaymentRow();
      }
    }
  };

  const handlePaymentTypeChange = (type) => {
    setPaymentTypes({
      ...paymentTypes,
      [type]: !paymentTypes[type],
    });
  };

  // Close payment dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        paymentDropdownRef.current &&
        !paymentDropdownRef.current.contains(event.target)
      ) {
        setIsPaymentDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [paymentDropdownRef]);

  const getSelectedPaymentTypes = () => {
    const selected = Object.entries(paymentTypes)
      .filter(([_, isSelected]) => isSelected)
      .map(([type, _]) => {
        // Format the payment type label
        if (type === "bankTransfer") return "Bank Transfer";
        return type.charAt(0).toUpperCase() + type.slice(1);
      });

    return selected.length > 0 ? selected.join(", ") : "Select Payment Type";
  };

  // Payment details state
  const [paymentDetails, setPaymentDetails] = useState({
    cash: { amount: "" },
    card: { amount: "" },
    upi: { amount: "" },
    bankTransfer: { amount: "" },
    other: { kashar: "" },
  });

  const handlePaymentDetailChange = (type, field, value) => {
    setPaymentDetails({
      ...paymentDetails,
      [type]: {
        ...paymentDetails[type],
        [field]: value,
      },
    });
  };

  // Payment completion tracking state
  const [followUpDate, setFollowUpDate] = useState("");
  const [paymentRemarks, setPaymentRemarks] = useState("");

  // Calculate total amount from payment rows
  const calculatePaymentTotal = () => {
    return paymentRows
      .reduce((sum, row) => {
        const amount = parseFloat(row.amount) || 0;
        return sum + amount;
      }, 0)
      .toFixed(2);
  };

  // Calculate remaining amount (Grand Total - Payment Total)
  const calculateRemainingAmount = () => {
    // Get grand total without the currency symbol
    const grandTotal = parseFloat(
      calculateGrandTotal().replace(/[^\d.-]/g, "")
    );
    const paymentTotal = parseFloat(calculatePaymentTotal());

    const remaining = grandTotal - paymentTotal;
    // Return 0 if negative or very small number (accounting for floating point errors)
    return Math.max(0, remaining.toFixed(2));
  }
  const calculatePayableAmount = () => {
    const totalPaid = parseFloat(calculatePaymentTotal()) || 0;
    const grandTotal =
      parseFloat(String(calculateGrandTotal()).replace(/[^\d.-]/g, "")) || 0;
    if (totalPaid > grandTotal) {
      return (totalPaid - grandTotal).toFixed(2);
    }
    return "0.00";
  };

  ;

  // Add CSS to remove number input spinners
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      /* Remove number input spinners */
      input[type="number"]::-webkit-inner-spin-button,
      input[type="number"]::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      input[type="number"] {
        -moz-appearance: textfield;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Handle round-off amount change (any edit locks manual mode; empty = no round off)
  const handleRoundOffChange = (e) => {
    handleRoundOffInputChange(
      e,
      (value) => {
        setIsManualRoundOff(true);
        setRoundOffAmount(value);
      },
      {
        allowNegative: true,
        simple: true,
      },
    );
  };

  // Handle GST percentage change
  const handleGstPercentChange = (e) => {
    const value = e.target.value;
    // Allow only numbers and decimal point
    if (/^\d*\.?\d*$/.test(value) || value === "") {
      setGstPercent(value);
    }
  };

  // Auto-calculate GST Amount when GST % or Final Amount changes
  useEffect(() => {
    const finalAmount = calculateTotal();
    const gstPercentValue = parseFloat(gstPercent) || 0;
    const calculatedGstAmt = (finalAmount * gstPercentValue) / 100;
    setGstAmount(calculatedGstAmt.toFixed(2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gstPercent, tableData]);

  // Recalculate ItRate and ItAmt when live master rates change
  useEffect(() => {
    if (!tableData || tableData.length === 0) return;
    setTableData((prev) => {
      let updated = [...prev];
      for (let i = 0; i < updated.length; i++) {
        const pgCell = updated[i].data.find((c) => c.id === "productGroup");
        if (!pgCell || !pgCell.value) continue;
        updated = calculateAndUpdateItRate(updated, i);
      }
      return updated;
    });
  }, [masterGoldRate, masterSilverRate]);

  // Handle note change
  const handleNoteChange = (e) => {
    const value = e.target.value;
    if (value.length <= 70) {
      setNote(value);
    }
  };

  const handleDeleteRow = (rowIndex) => {
    setTableData((prevData) => {
      const newData = prevData.filter((_, index) => index !== rowIndex);
      return newData;
    });
  };

  // Add expDueDate state
  const [expDueDate, setExpDueDate] = useState("");

  // Handler for Exp Due Date
  const handleExpDueDateChange = (e) => {
    setExpDueDate(e.target.value);
  };

  // Handle karigar name input change
  const handleKarigarNameChange = (e) => {
    const value = e.target.value;
    setKarigarNameSearchText(value);
    setKarigarNameDropdownActive(true);
    setKarigarNameHighlightedIndex(0);
    setSelectedKarigarId(null);
  };

  // Get filtered karigars based on name
  const getFilteredKarigarsByName = (searchText) => {
    // Only show active karigars
    const activeKarigars = getActiveItems(karigars, {
      statusField: "status",
      activeValue: "1",
    });
    if (!searchText) return activeKarigars;
    return activeKarigars.filter((karigar) => {
      const displayName = karigar.companyName
        ? `${karigar.fullName} (${karigar.companyName})`
        : karigar.fullName;
      return (
        karigar.fullName.toLowerCase().includes(searchText.toLowerCase()) ||
        karigar.companyName.toLowerCase().includes(searchText.toLowerCase()) ||
        karigar.shortCode.toLowerCase().includes(searchText.toLowerCase()) ||
        displayName.toLowerCase().includes(searchText.toLowerCase())
      );
    });
  };

  // Function to handle karigar name selection
  const handleKarigarNameSelect = (selectedItem) => {
    const displayName = selectedItem.companyName
      ? `${selectedItem.fullName} (${selectedItem.companyName})`
      : selectedItem.fullName;
    setKarigarName(displayName);
    setKarigarNameSearchText(displayName);
    setKarigarNameDropdownActive(false);
    setSelectedKarigarId(selectedItem.id);
  };

  // Handle key navigation in karigar name dropdown
  const handleKarigarNameKeyDown = (e) => {
    const filtered = getFilteredKarigarsByName(karigarNameSearchText);

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setKarigarNameHighlightedIndex((prevIndex) => {
          const newIndex =
            prevIndex < filtered.length - 1 ? prevIndex + 1 : prevIndex;
          // Scroll the highlighted item into view
          if (karigarNameItemsRef.current[newIndex]) {
            karigarNameItemsRef.current[newIndex].scrollIntoView({
              block: "nearest",
              behavior: "smooth",
            });
          }
          return newIndex;
        });
        break;
      case "ArrowUp":
        e.preventDefault();
        setKarigarNameHighlightedIndex((prevIndex) => {
          const newIndex = prevIndex > 0 ? prevIndex - 1 : 0;
          // Scroll the highlighted item into view
          if (karigarNameItemsRef.current[newIndex]) {
            karigarNameItemsRef.current[newIndex].scrollIntoView({
              block: "nearest",
              behavior: "smooth",
            });
          }
          return newIndex;
        });
        break;
      case "Enter":
        e.preventDefault();
        if (filtered.length > 0 && karigarNameDropdownActive) {
          handleKarigarNameSelect(filtered[karigarNameHighlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setKarigarNameDropdownActive(false);
        break;
      case "Tab":
        setKarigarNameDropdownActive(false);
        break;
      default:
        break;
    }
  };

  // Render karigar name dropdown
  const renderKarigarNameDropdown = () => {
    if (!karigarNameDropdownActive) return null;

    const filtered = getFilteredKarigarsByName(karigarNameSearchText);

    // Reset refs array
    karigarNameItemsRef.current = [];

    // Calculate width based on input field
    const inputWidth = karigarNameInputRef.current
      ? karigarNameInputRef.current.offsetWidth
      : 150;
    const inputRect = karigarNameInputRef.current
      ? karigarNameInputRef.current.getBoundingClientRect()
      : null;

    if (!inputRect) return null;

    return ReactDOM.createPortal(
      <div
        ref={karigarNameDropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "absolute",
          top: `${inputRect.bottom}px`,
          left: `${inputRect.left}px`,
          width: `${inputWidth}px`,
          minWidth: `${inputWidth}px`,
          maxWidth: `${inputWidth}px`,
          maxHeight: "150px", // Reduced from 200px to show ~5 items
          overflowY: "auto",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          padding: "0.5rem 0",
          backgroundColor: "#fff",
        }}
      >
        {filtered.length > 0 ? (
          filtered.map((item, idx) => {
            const isSelected =
              item.fullName.toLowerCase() ===
              karigarNameSearchText.toLowerCase();

            return (
              <div
                key={idx}
                ref={(el) => (karigarNameItemsRef.current[idx] = el)}
                className={`dropdown-item ${idx === karigarNameHighlightedIndex ? "active" : ""
                  }`}
                title={`${item.fullName}${item.companyName ? ` (${item.companyName})` : ""
                  }`}
                style={{
                  cursor: "pointer",
                  padding: "0.25rem 1rem",
                  fontSize: "0.85rem",
                  backgroundColor:
                    idx === karigarNameHighlightedIndex
                      ? "var(--theme-primary)"
                      : isSelected
                        ? "#f0f0f0"
                        : "transparent",
                  color:
                    idx === karigarNameHighlightedIndex ? "#fff" : "inherit",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  transition: "background-color 0.2s, color 0.2s", // Add transition for smooth highlight
                }}
                onMouseEnter={() => setKarigarNameHighlightedIndex(idx)}
                onMouseDown={() => handleKarigarNameSelect(item)}
              >
                {item.fullName} {item.companyName && `(${item.companyName})`}
              </div>
            );
          })
        ) : (
          <div className="dropdown-item disabled">No matching karigars</div>
        )}
      </div>,
      document.getElementById("dropdown-portal")
    );
  };

  // --- Add state for Karigar Add Modal ---
  const [showAddKarigarModal, setShowAddKarigarModal] = useState(false);
  const [karigarFullName, setKarigarFullName] = useState("");
  const [karigarCompanyName, setKarigarCompanyName] = useState("");
  const [karigarShortCode, setKarigarShortCode] = useState("");
  const [karigarContactNumber, setKarigarContactNumber] = useState("");
  const [karigarEmail, setKarigarEmail] = useState("");
  const [karigarAddress, setKarigarAddress] = useState("");
  const [karigarCity, setKarigarCity] = useState("");
  const [karigarGstNumber, setKarigarGstNumber] = useState("");
  const [karigarStatus, setKarigarStatus] = useState("active");
  const [karigarSaving, setKarigarSaving] = useState(false);

  // Generate short code from company name
  useEffect(() => {
    setKarigarShortCode(
      karigarCompanyName
        .split(" ")
        .filter((word) => word && !word.includes("-"))
        .map((word) => word.charAt(0).toUpperCase())
        .join("")
    );
  }, [karigarCompanyName]);

  // --- Add Karigar Modal submit handler ---
  const handleAddKarigarSubmit = async (e) => {
    e.preventDefault();

    const karigarMobileError = getMobileValidationError(karigarContactNumber, {
      required: true,
    });
    if (karigarMobileError) {
      toast.error(karigarMobileError);
      return;
    }

    const karigarEmailError = getEmailValidationError(karigarEmail);
    if (karigarEmailError) {
      toast.error(karigarEmailError);
      return;
    }

    setKarigarSaving(true);
    const requestData = {
      name: karigarFullName,
      company_name: karigarCompanyName,
      short_code: karigarShortCode,
      contact_number: karigarContactNumber,
      email: karigarEmail,
      address: karigarAddress,
      city: karigarCity,
      gst_number: karigarGstNumber,
      primary_supplier: karigarStatus === "active" ? 0 : 0,
    };
    // console.log("Sending Karigar request data:", requestData);
    try {
      const response = await createSupplier(requestData);
      if (response?.success) {
        toast.success("Karigar added successfully");
        setShowAddKarigarModal(false);
        // Reset form fields...

        setKarigarFullName("");
        setKarigarCompanyName("");
        setKarigarShortCode("");
        setKarigarContactNumber("");
        setKarigarEmail("");
        setKarigarAddress("");
        setKarigarCity("");
        setKarigarGstNumber("");
        setKarigarStatus("active");
        fetchKarigars();
      } else {
        toast.error(response?.message || "Failed to add karigar");
      }
    } catch (error) {
      console.error("Karigar API error:", error, error.response?.data);
      toast.error(error.response?.data?.message || "Failed to add karigar");
    } finally {
      setKarigarSaving(false);
    }
  };

  return (
    <div className={`body-wrapper ${cardClass}`}>
      <div className="container-fluid">
        <div className={`card shadow-sm ${cardClass}`}>
          <div className="px-4 py-3 border-bottom">
            <div className="d-flex align-items-center flex-wrap gap-3 w-100">
              <h4 className="card-title mb-0">Add Custom Order</h4>
              <div className="d-flex align-items-center flex-shrink-0">
                <div className="d-flex align-items-center">
                  <label
                    className="form-label me-2 mb-0 text-nowrap"
                    style={{
                      minWidth: "74px",
                      fontSize: "0.85rem",
                      fontWeight: "600",
                    }}
                  >
                    Bill Type:
                  </label>
                  <div style={billTypeHeaderSelectWrapStyle}>
                    <Select
                      styles={billTypeHeaderSelectStyles}
                      menuPlacement="bottom"
                      isSearchable={false}
                      options={billTypes
                        .filter(
                          (item) =>
                            String(item.id) !== String(selectedBillTypeId || ""),
                        )
                        .map((item) => ({
                          value: String(item.id),
                          label: item.name,
                        }))}
                      value={
                        (() => {
                          const selected =
                            billTypes.find(
                              (item) =>
                                String(item.id) === String(selectedBillTypeId),
                            ) ||
                            billTypes.find(
                              (item) => item.name.toLowerCase() === "gst",
                            ) ||
                            null;
                          return selected
                            ? {
                              value: String(selected.id),
                              label: selected.name,
                            }
                            : null;
                        })()
                      }
                      onChange={(option) => {
                        const selected = billTypes.find(
                          (item) => String(item.id) === String(option?.value),
                        );
                        applyBillTypeSelection(selected);
                      }}
                    />
                  </div>
                </div>
              </div>
              {/* Date Component */}
              <div className="d-flex align-items-center flex-shrink-0">
                <label
                  className="form-label me-2 mb-0 text-nowrap"
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: "600",
                  }}
                >
                  Date:
                </label>
                <input
                  type="date"
                  className={`form-control-date form-control-date-compact form-control-sm ${buttonClass}`}
                  value={billDate}
                  onChange={handleDateChange}
                  style={{ fontSize: "0.85rem" }}
                />
              </div>
              {/* Exp Delivery Date Component */}
              <div className="d-flex align-items-center flex-shrink-0">
                <label
                  className="form-label me-2 mb-0 text-nowrap"
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: "600",
                  }}
                >
                  Exp Delivery:
                </label>
                <input
                  type="date"
                  className={`form-control-date form-control-date-compact form-control-sm ${buttonClass}`}
                  value={expDate}
                  onChange={handleExpDateChange}
                  style={{ fontSize: "0.85rem" }}
                />
              </div>
              {(selectedCustomerId || productGroupPoolHeader) && (
                <div className="d-flex align-items-center flex-wrap gap-2 ms-auto">
                  {selectedCustomerId && (
                    <>
                      <span
                        className={`badge ${parseFloat(customerDetails.balance) >= 0
                          ? "bg-success-subtle text-success"
                          : "bg-danger-subtle text-danger"
                          }`}
                        style={{ fontSize: "0.85rem" }}
                      >
                        Balance: ₹
                        {parseFloat(customerDetails.balance || 0).toFixed(2)}
                      </span>
                      <span
                        className={`badge ${parseFloat(customerDetails.saleBalance) > 0
                          ? "bg-danger-subtle text-danger"
                          : "bg-success-subtle text-success"
                          } ms-2`}
                        style={{ fontSize: "0.85rem" }}
                      >
                        Pending: ₹
                        {parseFloat(customerDetails.saleBalance || 0).toFixed(2)}
                      </span>
                    </>
                  )}
                  <ProductGroupPoolBalance
                    balance={productGroupPoolHeader}
                    className=""
                  />
                </div>
              )}
            </div>
          </div>
          <div className="card-body">
            <CustomerSelection
              customerMobile={customerMobile}
              setCustomerMobile={setCustomerMobile}
              customerMobileSearchText={customerMobileSearchText}
              setCustomerMobileSearchText={setCustomerMobileSearchText}
              customerName={customerName}
              setCustomerName={setCustomerName}
              customerNameSearchText={customerNameSearchText}
              setCustomerNameSearchText={setCustomerNameSearchText}
              customerAddress={customerAddress}
              setCustomerAddress={setCustomerAddress}
              customerDetails={customerDetails}
              setCustomerDetails={setCustomerDetails}
              customerCity={customerCity}
              setCustomerCity={setCustomerCity}
              selectedCustomerId={selectedCustomerId}
              setSelectedCustomerId={setSelectedCustomerId}
              customerSaveSuccess={customerSaveSuccess}
              setCustomerSaveSuccess={setCustomerSaveSuccess}
              users={userss}
              setUsers={setUserss}
              states={states}
              cities={cities}
              selectedState={selectedState}
              setSelectedState={setSelectedState}
              buttonClass={buttonClass}
              savingCustomer={savingCustomer}
              mobileInvalid={customerMobileInvalid}
              onClearMobileInvalid={() => setCustomerMobileInvalid(false)}
            />

            <div
              ref={tableContainerRef}
              className={`table-responsive border rounded-0 mt-3 ${buttonClass}`}
              style={{
                maxHeight:
                  tableData.length > 6
                    ? `${40 + 6 * 38 + 38}px`
                    : undefined,
                overflowY: tableData.length > 6 ? "auto" : "visible",
              }}
            >
              {/* No table-rounded — overflow:hidden breaks sticky header/total */}
              <table className="table table-bordered table-hover table-sm mb-0 theme-header-table">
                <thead>
                  <tr>
                    {COLUMNS.map((column) => (
                      <th
                        key={column.id}
                        className="px-2 py-2 text-center"
                        style={{
                          fontSize: "0.85rem",
                          width: column.width,
                          fontWeight: "600",
                          whiteSpace: "nowrap",
                          position: tableData.length > 6 ? "sticky" : undefined,
                          top: tableData.length > 6 ? 0 : undefined,
                          zIndex: tableData.length > 6 ? 5 : undefined,
                        }}
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, rowIndex) => (
                    <tr key={row.id} style={{ backgroundColor: "#ffffff" }}>
                      {row.data.map((cell, colIndex) => {
                        // Add special handling for serial number column
                        if (cell.id === "no") {
                          return (
                            <td
                              key={cell.id}
                              className="p-0"
                              style={{ width: COLUMNS[colIndex]?.width }}
                            >
                              <div
                                className="d-flex align-items-center justify-content-center"
                                style={{ height: "38px" }}
                              >
                                <span
                                  style={{
                                    fontSize: "0.85rem",
                                    cursor: "pointer",
                                    transition: "opacity 0.2s",
                                  }}
                                  onClick={() => handleDeleteRow(rowIndex)}
                                  onMouseEnter={(e) => {
                                    e.target.textContent = "×";
                                    e.target.style.fontWeight = "600";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.textContent = rowIndex + 1;
                                    e.target.style.fontWeight = "normal";
                                  }}
                                >
                                  {rowIndex + 1}
                                </span>
                              </div>
                            </td>
                          );
                        }

                        // Check if this is the LWt column and if there are Other Amount entries
                        const hasOtherAmountEntries =
                          row.data.find((c) => c.id === "othAmt")
                            ?.otherAmountEntries?.length > 0;
                        const hasFieldError = !!itemFieldErrors[row.id]?.includes(
                          cell.id,
                        );

                        return (
                          <td
                            key={cell.id}
                            className="p-0"
                            style={{
                              width: COLUMNS[colIndex]?.width,
                              position: "relative",
                              ...lineCellErrorStyle(hasFieldError),
                            }}
                          >
                            {isDropdownColumn(getColumnType(colIndex)) ? (
                              // Dropdown-enabled cell
                              <div className="position-relative">
                                <input
                                  ref={(el) =>
                                  (inputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    handleInputChange(
                                      rowIndex,
                                      colIndex,
                                      newValue
                                    );
                                    // Show dropdown when typing, but for productGroup only after 3+ characters
                                    const columnType = getColumnType(colIndex);

                                    // Check if this is a column that requires 3+ characters
                                    const requiresMinLength =
                                      columnType === "productGroup";
                                    const meetsMinLength =
                                      !requiresMinLength ||
                                      newValue.length >= 3;

                                    // Only show dropdown if minimum length is met (or not required)
                                    if (meetsMinLength) {
                                      setTimeout(() => {
                                        handleDropdownClick(
                                          rowIndex,
                                          colIndex,
                                          columnType
                                        );
                                      }, 0);
                                    } else {
                                      // Close dropdown if less than 3 characters
                                      setActiveDropdown({
                                        row: null,
                                        col: null,
                                        type: null,
                                      });
                                    }
                                  }}
                                  onKeyDown={(e) =>
                                    handleKeyDown(
                                      e,
                                      rowIndex,
                                      colIndex,
                                      getColumnType(colIndex)
                                    )
                                  }
                                  onClick={() => {
                                    const columnType = getColumnType(colIndex);
                                    // For productGroup, only show dropdown if 3+ characters
                                    if (columnType === "productGroup") {
                                      if (
                                        cell.value &&
                                        cell.value.length >= 3
                                      ) {
                                        handleDropdownClick(
                                          rowIndex,
                                          colIndex,
                                          columnType
                                        );
                                      }
                                    } else {
                                      // For other columns, show dropdown normally
                                      handleDropdownClick(
                                        rowIndex,
                                        colIndex,
                                        columnType
                                      );
                                    }
                                  }}
                                  className={`form-control border-0 rounded-0 ${buttonClass}`}
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                  }}
                                  onFocus={() =>
                                    handleFocus(rowIndex, colIndex)
                                  }
                                  onBlur={(e) => {
                                    e.target.style.backgroundColor =
                                      "transparent";
                                  }}
                                  autoComplete="off"
                                />
                              </div>
                            ) : isImageColumn(getColumnType(colIndex)) ? (
                              <div
                                className="position-relative d-flex align-items-center justify-content-center"
                                style={{ height: "38px" }}
                              >
                                <input
                                  type="file"
                                  accept="image/*"
                                  ref={(el) =>
                                  (fileInputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                  }
                                  style={{ display: "none" }}
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;

                                    // Check if file is an image
                                    if (!file.type.match("image.*")) {
                                      alert("Please select an image file");
                                      return;
                                    }

                                    // Check file size (limit to 5MB)
                                    if (file.size > 5 * 1024 * 1024) {
                                      alert("File size should not exceed 5MB");
                                      return;
                                    }

                                    handleImageUpload(file, rowIndex);
                                  }}
                                />
                                {cell.imagePreview ? (
                                  <div
                                    className="position-relative"
                                    style={{ height: "38px" }}
                                  >
                                    <div
                                      className="d-flex justify-content-center align-items-center h-100"
                                      onMouseEnter={(e) => {
                                        // Clear any existing timeout
                                        if (tooltipTimeoutRef.current) {
                                          clearTimeout(
                                            tooltipTimeoutRef.current
                                          );
                                          tooltipTimeoutRef.current = null;
                                        }

                                        // Hide all tooltips first
                                        Object.values(
                                          tooltipRefs.current
                                        ).forEach((tooltip) => {
                                          if (tooltip)
                                            tooltip.style.display = "none";
                                        });

                                        const tooltipId = `tooltip-${rowIndex}-${colIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // Get the position of the image
                                          const rect =
                                            e.currentTarget.getBoundingClientRect();

                                          // Position the tooltip centered below the image
                                          tooltip.style.position = "fixed";
                                          tooltip.style.left =
                                            rect.left +
                                            rect.width / 2 -
                                            100 +
                                            "px"; // Center horizontally (tooltip width is 200px)
                                          tooltip.style.top =
                                            rect.bottom + 10 + "px"; // Position below the image
                                          tooltip.style.display = "block";
                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        // Set a timeout before hiding the tooltip
                                        tooltipTimeoutRef.current = setTimeout(
                                          () => {
                                            // Only hide if this is still the active tooltip
                                            const tooltipId = `tooltip-${rowIndex}-${colIndex}`;
                                            if (activeTooltip === tooltipId) {
                                              const tooltip =
                                                tooltipRefs.current[tooltipId];
                                              if (tooltip) {
                                                tooltip.style.display = "none";
                                              }
                                              setActiveTooltip(null);
                                            }
                                          },
                                          300
                                        ); // Delay to allow moving to tooltip
                                      }}
                                    >
                                      <img
                                        src={cell.imagePreview}
                                        alt="Preview"
                                        className="img-thumbnail"
                                        data-raw-path={cell.value || ""}
                                        data-candidate-index="0"
                                        onError={handleProductImageError}
                                        style={{
                                          height: "30px",
                                          width: "30px",
                                          objectFit: "cover",
                                          cursor: "pointer",
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openImageModal(
                                            cell.imagePreview,
                                            cell.value,
                                          );
                                        }}
                                      />
                                    </div>

                                    {/* Hover preview tooltip */}
                                    <div
                                      ref={(el) =>
                                      (tooltipRefs.current[
                                        `tooltip-${rowIndex}-${colIndex}`
                                      ] = el)
                                      }
                                      className="shadow rounded p-3 bg-white"
                                      style={{
                                        display: "none",
                                        width: "200px",
                                        zIndex: 9999,
                                        position: "fixed", // Use fixed positioning to escape table constraints
                                      }}
                                      onMouseEnter={(e) => {
                                        // Clear any existing timeout when entering the tooltip
                                        if (tooltipTimeoutRef.current) {
                                          clearTimeout(
                                            tooltipTimeoutRef.current
                                          );
                                          tooltipTimeoutRef.current = null;
                                        }

                                        // Make sure this is the only visible tooltip
                                        const currentTooltipId = `tooltip-${rowIndex}-${colIndex}`;
                                        Object.entries(
                                          tooltipRefs.current
                                        ).forEach(([id, tooltip]) => {
                                          if (
                                            tooltip &&
                                            id !== currentTooltipId
                                          ) {
                                            tooltip.style.display = "none";
                                          }
                                        });

                                        e.currentTarget.style.display = "block";
                                        setActiveTooltip(currentTooltipId);
                                      }}
                                      onMouseLeave={(e) => {
                                        // Hide immediately when leaving the tooltip itself
                                        e.currentTarget.style.display = "none";
                                        setActiveTooltip(null);
                                      }}
                                    >
                                      <div
                                        style={{
                                          position: "absolute",
                                          top: "-8px",
                                          left: "50%",
                                          transform: "translateX(-50%)",
                                          width: "16px",
                                          height: "8px",
                                          overflow: "hidden",
                                        }}
                                      >
                                        <div
                                          className="bg-white border"
                                          style={{
                                            position: "absolute",
                                            top: "0",
                                            left: "0",
                                            width: "16px",
                                            height: "16px",
                                            transform: "rotate(45deg)",
                                            boxShadow:
                                              "-2px -2px 2px rgba(0,0,0,0.1)",
                                            borderRight: "none",
                                            borderBottom: "none",
                                          }}
                                        ></div>
                                      </div>
                                      <div className="text-center mb-3">
                                        <img
                                          src={cell.imagePreview}
                                          alt="Preview"
                                          className="img-fluid rounded"
                                          data-raw-path={cell.value || ""}
                                          data-candidate-index="0"
                                          onError={handleProductImageError}
                                          style={{
                                            maxHeight: "100px",
                                            objectFit: "contain",
                                          }}
                                        />
                                      </div>
                                      <div className="d-flex justify-content-center gap-2">
                                        <Button
                                          variant="primary"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            triggerFileInput(
                                              rowIndex,
                                              colIndex
                                            );
                                          }}
                                          title="Change Image"
                                        >
                                          <Icon
                                            icon="solar:gallery-edit-bold"
                                            width="16"
                                            height="16"
                                          />
                                        </Button>
                                        <Button
                                          variant="danger"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setTableData((prevData) => {
                                              const updatedData = [...prevData];
                                              const imageCell = updatedData[
                                                rowIndex
                                              ].data.find(
                                                (c) => c.id === "image"
                                              );
                                              if (imageCell) {
                                                imageCell.value = "";
                                                imageCell.imageFile = null;
                                                imageCell.imagePreview = null;
                                              }
                                              return updatedData;
                                            });
                                          }}
                                          title="Remove Image"
                                        >
                                          <Icon
                                            icon="solar:trash-bin-trash-bold"
                                            width="16"
                                            height="16"
                                          />
                                        </Button>
                                        <Button
                                          variant="primary"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openImageModal(
                                              cell.imagePreview,
                                              cell.value,
                                            );
                                          }}
                                          title="View Image"
                                        >
                                          <Icon
                                            icon="solar:eye-bold"
                                            width="16"
                                            height="16"
                                          />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() =>
                                      triggerFileInput(rowIndex, colIndex)
                                    }
                                    title="Upload Image"
                                  >
                                    <Icon
                                      icon="solar:gallery-add-bold"
                                      width="16"
                                      height="16"
                                    />
                                  </Button>
                                )}
                              </div>
                            ) : getColumnType(colIndex) === "lsWt" ? (
                              <div className="d-flex align-items-center position-relative">
                                <input
                                  ref={(el) =>
                                  (inputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    // Only allow changes if there are no Other Amount entries
                                    if (!hasOtherAmountEntries) {
                                      const value = e.target.value;
                                      if (
                                        value === "" ||
                                        /^\d*\.?\d*$/.test(value)
                                      ) {
                                        handleInputChange(
                                          rowIndex,
                                          colIndex,
                                          value
                                        );
                                      }
                                    }
                                  }}
                                  className={`form-control border-0 rounded-0 ${buttonClass}`}
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                    cursor: hasOtherAmountEntries
                                      ? "not-allowed"
                                      : "text",
                                  }}
                                  onFocus={(e) => {
                                    if (!hasOtherAmountEntries) {
                                      handleFocus(rowIndex, colIndex);
                                    }
                                  }}
                                  onBlur={(e) => {
                                    e.target.style.backgroundColor =
                                      "transparent";
                                  }}
                                  readOnly={hasOtherAmountEntries}
                                  title={
                                    hasOtherAmountEntries
                                      ? "This value is calculated from Other Amount entries"
                                      : ""
                                  }
                                />
                              </div>
                            ) : getColumnType(colIndex) === "othAmt" ? (
                              <div className="d-flex align-items-center position-relative">
                                <input
                                  ref={(el) =>
                                  (inputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    // Only allow changes if there are no entries
                                    if (!cell.otherAmountEntries?.length) {
                                      const value = e.target.value;
                                      if (
                                        value === "" ||
                                        /^\d*\.?\d*$/.test(value)
                                      ) {
                                        handleInputChange(
                                          rowIndex,
                                          colIndex,
                                          value
                                        );
                                      }
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    handleKeyDown(
                                      e,
                                      rowIndex,
                                      colIndex,
                                      getColumnType(colIndex)
                                    );
                                    handleOtherAmountInteraction(rowIndex, e);
                                  }}
                                  onDoubleClick={(e) =>
                                    handleOtherAmountInteraction(rowIndex, e)
                                  }
                                  className={`form-control border-0 rounded-0 ${buttonClass}`}
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                    cursor: "pointer",
                                  }}
                                  onFocus={() =>
                                    handleFocus(rowIndex, colIndex)
                                  }
                                  onBlur={(e) =>
                                  (e.target.style.backgroundColor =
                                    "transparent")
                                  }
                                  title={
                                    cell.otherAmountEntries?.length > 0
                                      ? "Double-click or press Enter twice to edit details"
                                      : "Enter amount directly or double-click/press Enter twice for details"
                                  }
                                />

                                {cell.otherAmountEntries?.length > 0 && (
                                  <div className="position-relative">
                                    <Button
                                      variant="link"
                                      className="p-0 px-1 text-primary"
                                      style={{
                                        fontSize: "1rem",
                                        position: "absolute",
                                        right: "5px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        zIndex: 1,
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // If tooltip is already visible, hide it
                                          if (
                                            tooltip.style.display === "block"
                                          ) {
                                            tooltip.style.display = "none";
                                            setActiveTooltip(null);

                                            // Clear any existing timeout
                                            if (tooltipTimeoutRef.current) {
                                              clearTimeout(
                                                tooltipTimeoutRef.current
                                              );
                                              tooltipTimeoutRef.current = null;
                                            }
                                            return;
                                          }

                                          // Hide any other visible tooltips first
                                          Object.entries(
                                            tooltipRefs.current
                                          ).forEach(([id, t]) => {
                                            if (t && id !== tooltipId) {
                                              t.style.display = "none";
                                            }
                                          });

                                          // Position and show tooltip
                                          positionTooltip(
                                            e.currentTarget,
                                            tooltip
                                          );

                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);

                                          // Set timeout to hide after 4 seconds
                                          if (tooltipTimeoutRef.current) {
                                            clearTimeout(
                                              tooltipTimeoutRef.current
                                            );
                                          }

                                          tooltipTimeoutRef.current =
                                            setTimeout(() => {
                                              if (tooltip) {
                                                tooltip.style.display = "none";
                                                setActiveTooltip(null);
                                                tooltipTimeoutRef.current =
                                                  null;
                                              }
                                            }, 4000);
                                        }
                                      }}
                                      onMouseEnter={(e) => {
                                        // Don't show on hover if already showing from a click
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // Hide any other visible tooltips first
                                          Object.entries(
                                            tooltipRefs.current
                                          ).forEach(([id, t]) => {
                                            if (t && id !== tooltipId) {
                                              t.style.display = "none";
                                            }
                                          });

                                          // Position and show tooltip
                                          positionTooltip(
                                            e.currentTarget,
                                            tooltip
                                          );

                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);
                                        }
                                      }}
                                      onMouseLeave={() => {
                                        // Only hide if not in clicked mode
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (
                                          tooltip &&
                                          activeTooltip === tooltipId
                                        ) {
                                          // Use setTimeout to allow moving to the tooltip
                                          setTimeout(() => {
                                            // Only hide if we're not hovering over the tooltip itself
                                            if (
                                              activeTooltip === tooltipId &&
                                              !tooltip.matches(":hover") &&
                                              !tooltipTimeoutRef.current
                                            ) {
                                              tooltip.style.display = "none";
                                              setActiveTooltip(null);
                                            }
                                          }, 100);
                                        }
                                      }}
                                    >
                                      <Icon icon="solar:info-circle-bold" />
                                    </Button>

                                    {/* Other Amount Details Tooltip */}
                                    <div
                                      ref={(el) =>
                                      (tooltipRefs.current[
                                        `oth-amt-tooltip-${rowIndex}`
                                      ] = el)
                                      }
                                      className="shadow-lg rounded p-2 bg-white tooltip-container"
                                      style={{
                                        display: "none",
                                        width: "auto",
                                        maxWidth: "250px",
                                        zIndex: 9999,
                                        position: "fixed",
                                      }}
                                      onMouseEnter={() => {
                                        // Do nothing when hovering tooltip
                                        // This prevents the tooltip from disappearing
                                      }}
                                      onMouseLeave={(e) => {
                                        // Only hide if not in clicked mode
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        if (activeTooltip === tooltipId) {
                                          e.currentTarget.style.display =
                                            "none";
                                          setActiveTooltip(null);
                                        }
                                      }}
                                    >
                                      <div
                                        className="tooltip-arrow"
                                        style={{
                                          position: "absolute",
                                          width: "12px",
                                          height: "12px",
                                          backgroundColor: "white",
                                          border: "1px solid rgba(0,0,0,0.1)",
                                          transform: "rotate(45deg)",
                                          borderRight: "none",
                                          borderBottom: "none",
                                          boxShadow:
                                            "-2px -2px 2px rgba(0,0,0,0.1)",
                                        }}
                                      ></div>
                                      <div
                                        className="other-amount-details"
                                        style={{
                                          maxHeight: "200px",
                                          overflowY: "auto",
                                        }}
                                      >
                                        {cell.otherAmountEntries.map(
                                          (entry, idx) => (
                                            <div
                                              key={idx}
                                              className="d-flex justify-content-between py-1"
                                              style={{
                                                borderBottom:
                                                  idx !==
                                                    cell.otherAmountEntries
                                                      .length -
                                                    1
                                                    ? "1px solid #eee"
                                                    : "none",
                                              }}
                                            >
                                              <span
                                                style={{
                                                  fontSize: "0.85rem",
                                                  flex: "1",
                                                  marginRight: "20px",
                                                }}
                                              >
                                                {entry.chargeType}
                                              </span>
                                              <span
                                                style={{
                                                  fontSize: "0.85rem",
                                                  minWidth: "80px",
                                                  textAlign: "right",
                                                }}
                                              >
                                                ₹
                                                {Number(entry.amount).toFixed(
                                                  2
                                                )}
                                              </span>
                                            </div>
                                          )
                                        )}
                                        <div className="mt-2 pt-2 border-top">
                                          <div className="d-flex justify-content-between">
                                            <strong className="text-dark">
                                              Total
                                            </strong>
                                            <strong className="text-dark">
                                              ₹
                                              {cell.otherAmountEntries
                                                .reduce(
                                                  (sum, entry) =>
                                                    sum + Number(entry.amount),
                                                  0
                                                )
                                                .toFixed(2)}
                                            </strong>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : getColumnType(colIndex) === "lsWt" ? (
                              <div className="d-flex align-items-center position-relative">
                                <input
                                  ref={(el) =>
                                  (inputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    // Only allow changes if there are no Other Amount entries
                                    if (!hasOtherAmountEntries) {
                                      const value = e.target.value;
                                      if (
                                        value === "" ||
                                        /^\d*\.?\d*$/.test(value)
                                      ) {
                                        handleInputChange(
                                          rowIndex,
                                          colIndex,
                                          value
                                        );
                                      }
                                    }
                                  }}
                                  className={`form-control border-0 rounded-0 ${buttonClass}`}
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                    cursor: hasOtherAmountEntries
                                      ? "not-allowed"
                                      : "text",
                                  }}
                                  onFocus={(e) => {
                                    if (!hasOtherAmountEntries) {
                                      handleFocus(rowIndex, colIndex);
                                    }
                                  }}
                                  onBlur={(e) => {
                                    e.target.style.backgroundColor =
                                      "transparent";
                                  }}
                                  readOnly={hasOtherAmountEntries}
                                  title={
                                    hasOtherAmountEntries
                                      ? "This value is calculated from Other Amount entries"
                                      : ""
                                  }
                                />
                              </div>
                            ) : (
                              <input
                                ref={(el) =>
                                (inputRefs.current[
                                  `${rowIndex}-${colIndex}`
                                ] = el)
                                }
                                type="text"
                                value={cell.value}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  const columnType = COLUMNS[colIndex].id;

                                  // Special handling for design field - allow alphanumeric
                                  if (columnType === "design") {
                                    handleInputChange(
                                      rowIndex,
                                      colIndex,
                                      value
                                    );
                                  }
                                  // For numeric fields only allow numbers and decimal
                                  else if (
                                    value === "" ||
                                    /^\d*\.?\d*$/.test(value)
                                  ) {
                                    handleInputChange(
                                      rowIndex,
                                      colIndex,
                                      value
                                    );
                                  }
                                }}
                                // disabled={COLUMNS[colIndex].id === "grWt"}
                                className={`form-control border-0 rounded-0 ${buttonClass}`}
                                style={{
                                  fontSize: "0.85rem",
                                  padding: "0.4rem 0.5rem",
                                  backgroundColor: "transparent",
                                  width: "100%",
                                  transition: "background-color 0.2s",
                                  cursor: "text", // Change cursor to indicate it's editable
                                }}
                                onFocus={() => handleFocus(rowIndex, colIndex)}
                                onBlur={(e) => {
                                  e.target.style.backgroundColor =
                                    "transparent";
                                }}
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {/* Add Totals Row */}
                  <tr
                    style={{
                      backgroundColor: "#f8f9fa",
                      position: tableData.length > 6 ? "sticky" : undefined,
                      bottom: tableData.length > 6 ? 0 : undefined,
                      zIndex: tableData.length > 6 ? 5 : undefined,
                    }}
                  >
                    {/* No. */}
                    <td className="p-0" style={{ width: COLUMNS[0]?.width }}>
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          Total
                        </span>
                      </div>
                    </td>
                    <td className="p-0"></td> {/* Product Group */}
                    <td className="p-0"></td> {/* Design */}
                    <td className="p-0"></td> {/* Size */}
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Pcs */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData.reduce((sum, row) => {
                            const pcs = parseFloat(
                              row.data.find((cell) => cell.id === "pcs")
                                ?.value || 0
                            );
                            return sum + (isNaN(pcs) ? 0 : pcs);
                          }, 0)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* GWt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const gwt = parseFloat(
                                row.data.find((cell) => cell.id === "grWt")
                                  ?.value || 0
                              );
                              return sum + (isNaN(gwt) ? 0 : gwt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* LWt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const lwt = parseFloat(
                                row.data.find((cell) => cell.id === "lsWt")
                                  ?.value || 0
                              );
                              return sum + (isNaN(lwt) ? 0 : lwt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* NWt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const nwt = parseFloat(
                                row.data.find((cell) => cell.id === "netWt")
                                  ?.value || 0
                              );
                              return sum + (isNaN(nwt) ? 0 : nwt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0"></td> {/* ItRateOn */}
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* ItRate */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {/* Average item rate - not sum */}
                          {tableData.length > 0
                            ? (
                              tableData.reduce((sum, row) => {
                                const itRate = parseFloat(
                                  row.data.find(
                                    (cell) => cell.id === "itRate"
                                  )?.value || 0
                                );
                                return sum + (isNaN(itRate) ? 0 : itRate);
                              }, 0) / tableData.length
                            ).toFixed(2)
                            : "0.00"}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* ItAmt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const itAmt = parseFloat(
                                row.data.find((cell) => cell.id === "itAmt")
                                  ?.value || 0
                              );
                              return sum + (isNaN(itAmt) ? 0 : itAmt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0"></td> {/* Lbr Type */}
                    <td className="p-0"></td> {/* Lbr % */}
                    <td className="p-0"></td> {/* Lbr Rate */}
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Lbr Amt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const lbrAmt = parseFloat(
                                row.data.find((cell) => cell.id === "lbrAmt")
                                  ?.value || 0
                              );
                              return sum + (isNaN(lbrAmt) ? 0 : lbrAmt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* OthAmt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const othAmt = parseFloat(
                                row.data.find((cell) => cell.id === "othAmt")
                                  ?.value || 0
                              );
                              return sum + (isNaN(othAmt) ? 0 : othAmt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* MRP */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const mrp = parseFloat(
                                row.data.find((cell) => cell.id === "mrp")
                                  ?.value || 0
                              );
                              return sum + (isNaN(mrp) ? 0 : mrp);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Image — auto round-off indicator */}
                      {tableTotalAutoRound.roundOff !== 0 && (
                        <div
                          className="d-flex align-items-center justify-content-end px-1 gap-1"
                          style={{ height: "38px" }}
                        >
                          <span
                            style={{
                              fontSize: "0.65rem",
                              color: "#6c757d",
                              fontWeight: 500,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatAutoRoundOffIndicator(
                              tableTotalAutoRound.roundOff,
                            )}
                          </span>
                          <span
                            style={{ fontSize: "0.6rem", color: "#adb5bd" }}
                            aria-hidden="true"
                          >
                            →
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* TotalAmt — auto round-off applied (same as Sales) */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableTotalAutoRound.rounded.toFixed(2)}
                        </span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="text-end mt-3">
              {/* <button className="btn btn-primary" onClick={handleSubmit}>
                Add
              </button> */}
            </div>


            <PaymentSection
              paymentRows={paymentRows}
              onPaymentRowsChange={setPaymentRows}
              paymentTypes={paymentTypes}
              bankAccounts={bankAccounts}
              customerId={selectedCustomerId}
              payableRows={payableRows}
              onPayableRowsChange={setPayableRows}
              note={note}
              onNoteChange={setNote}
              finalAmount={calculateTotal().toFixed(2)}
              showDiscount={false}
              gstPercentage={gstPercent}
              onGstPercentageChange={setGstPercent}
              gstAmount={
                typeof gstAmount === "string"
                  ? gstAmount
                  : Number(gstAmount || 0).toFixed(2)
              }
              roundOffAmount={getDisplayRoundOff()}
              onRoundOffChange={handleRoundOffChange}
              grandTotal={calculateGrandTotal()}
              totalPaid={parseFloat(calculatePaymentTotal()) || 0}
              payableAmount={calculatePayableAmount()}
              remainingAmount={calculateRemainingAmount()}
              followUpDate={followUpDate}
              onFollowUpDateChange={setFollowUpDate}
              buttonClass={buttonClass}
              cardClass={cardClass}
              buttonTableStyle={buttonTableStyle}
            />

            {/* Submit button at the bottom */}
            <div className="row mt-4">
              <div className="col-12">
                <div className="d-flex flex-wrap align-items-center justify-content-between">
                  <div className="d-flex flex-wrap align-items-center">
                    <div className="form-check me-3 mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="printCheckbox"
                      />
                      <label
                        className="form-check-label d-flex align-items-center"
                        htmlFor="printCheckbox"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi"
                          viewBox="0 0 24 24"
                        >
                          <g
                            fill="none"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                          >
                            <path d="M17 17h2a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h2m10-8V5a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2v4" />
                            <path d="M7 15a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2z" />
                          </g>
                        </svg>
                        <span className="ms-2 d-none d-sm-inline">Print</span>
                      </label>
                    </div>
                    <div className="form-check me-3 mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="pdfCheckbox"
                      />
                      <label
                        className="form-check-label d-flex align-items-center"
                        htmlFor="pdfCheckbox"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi"
                          viewBox="0 0 24 24"
                        >
                          <g
                            fill="none"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                          >
                            <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                            <path d="M5 12V5a2 2 0 0 1 2-2h7l5 5v4M5 18h1.5a1.5 1.5 0 0 0 0-3H5v6m12-3h2m1-3h-3v6m-6-6v6h1a2 2 0 0 0 2-2v-2a2 2 0 0 0-2-2z" />
                          </g>
                        </svg>
                        <span className="ms-2 d-none d-sm-inline">PDF</span>
                      </label>
                    </div>
                    <div className="form-check me-3 mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="whatsappCheckbox"
                      />
                      <label
                        className="form-check-label d-flex align-items-center"
                        htmlFor="whatsappCheckbox"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi"
                          viewBox="0 0 24 24"
                        >
                          <g
                            fill="none"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                          >
                            <path d="m3 21l1.65-3.8a9 9 0 1 1 3.4 2.9z" />
                            <path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0za5 5 0 0 0 5 5h1a.5.5 0 0 0 0-1h-1a.5.5 0 0 0 0 1" />
                          </g>
                        </svg>
                        <span className="ms-2 d-none d-sm-inline">
                          WhatsApp
                        </span>
                      </label>
                    </div>
                    <div className="form-check me-3 mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="emailCheckbox"
                      />
                      <label
                        className="form-check-label d-flex align-items-center"
                        htmlFor="emailCheckbox"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi"
                          viewBox="0 0 24 24"
                        >
                          <g
                            fill="none"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                          >
                            <path d="M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                            <path d="m3 7l9 6l9-6" />
                          </g>
                        </svg>
                        <span className="ms-2 d-none d-sm-inline">Email</span>
                      </label>
                    </div>
                  </div>
                  {canAddCustomOrder && (
                    <button
                      className={`btn btn-primary ${buttonClass}`}
                      onClick={handleSubmit}
                    >
                      Save
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {renderDropdown()}
      {renderImageModal()}
      <OtherAmountModal
        otherAmountModal={otherAmountModal}
        setOtherAmountModal={setOtherAmountModal}
        cardClass={cardClass}
        otherAmountColumnWidths={otherAmountColumnWidths}
        handleOtherAmountColumnWidthChange={handleOtherAmountColumnWidthChange}
        suggestions={suggestions}
        setSuggestions={setSuggestions}
        getSuggestions={getSuggestions}
        applyChargeType={(entry, chargeName) =>
          applyOtherChargeToEntry(entry, chargeTypes, chargeName)
        }
        chargeOptions={chargeTypes}
        formatAmount={formatAmount}
        calculateAmount={calculateAmount}
        rateOnOptions={rateOnOptions}
        highlightedIndex={highlightedIndex}
        setHighlightedIndex={setHighlightedIndex}
        handleRateOnDropdown={handleRateOnDropdown}
        handleRateOnKeyDown={handleRateOnKeyDown}
        calculateLessWeight={calculateLessWeight}
        formatLessWeight={formatLessWeight}
        calculateTotal={sumOtherAmountEntries}
        buttonClass={buttonClass}
        handleSaveOtherAmountEntries={handleSaveOtherAmountEntries}
        setTableData={setTableData}
      />
      {/* <p>Master Gold Rate: {masterGoldRate}</p>
      <p>Master Silver Rate: {masterSilverRate}</p> */}
      <Toaster position="top-center" />
    </div>
  );
};

export default CustomOrderEntry;
